<template>
  <div id="dashboard-patients-table">
    <Breadcrumbs
      :breadcrumbs="[
        {title: this.$t(`menu.${this.serviceSlug}`)},
        {title: this.$t('menu.checkup_plus/patients'), active: true, meta: {type: 'string', value: `/check-up_plus/checkup_plus/patients`}},
        {title: handlePatientName, disabled: true}
      ]">

      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <va-dropdown
          v-if="hasSomeActionsPermissions"
          class="custom-dropdown"
          fixed>
          <span slot="anchor">
            <slot>
              <div class="pl-3 pr-2 clickable">
                <span class="fa fa-ellipsis-v" style="font-size: 1.5rem;"></span>
              </div>
            </slot>
          </span>
          <div class="contact-dropdown__content pl-3 pr-2 pt-2 pb-2">
            <div v-if="canUpdateUser" class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                 @click.prevent="handleUpdatePatientPm">
              <div>
                <i class="fa fa-pencil"></i>
                {{ $t('dashboard_new_evollu.actions.edit_patient') }}
              </div>
            </div>
            <div v-if="canDeletePatient" class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                 @click.prevent="showStatusChangePatientModal = true">
              <div v-if="patientDetails !== null && patientDetails.status === 1">
                <i class="fa fa-lock"></i>
                {{ $t('dashboard_new_evollu.actions.disable_patient') }}
              </div>
              <div v-else>
                <i class="fa fa-unlock"></i>
                {{ $t('dashboard_new_evollu.actions.enable_patient') }}
              </div>
            </div>
            <div v-if="canDeletePatient" class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                 @click.prevent="showDeletePatient = true">
              <div>
                <i class="fa fa-times-circle"></i>
                {{ $t('dashboard_new_evollu.actions.delete_patient') }}
              </div>
            </div>
          </div>
        </va-dropdown>

        <UpdatePatient
          v-if="showUpdatePatient"
          :patientDetailsToEdit="this.patientDetails"
          @cancel="onCancelEdit"
        />

        <ConfirmationModal
          v-if="showStatusChangePatientModal"
          :title="$t('dashboard_new_evollu.modal.title_disable_patient')"
          :body="$t('dashboard_new_evollu.modal.modal_disable_patient', {name: patientDetails.name})"
          okClass="btn-danger"
          @modal-confirmed="onConfirmChangeStatus"
          @modal-canceled="onCancelChangeStatus"
        />
        <ConfirmationModal
          v-if="showDeletePatient"
          :title="$t('dashboard_new_evollu.modal.title_delete_patient')"
          :body="$t('dashboard_new_evollu.modal.modal_delete_patient', {name: patientDetails.name})"
          okClass="btn-danger"
          @modal-confirmed="onConfirmDeletePatient"
          @modal-canceled="onCancelDeletePatient"
        />

      </div>
    </Breadcrumbs>

    <div class="text-center" v-if="isLoadingPatientData">
      <LoadingAnimation/>
    </div>

    <div class="overlay-loader" v-if="downloadPdfReportLoading">
      <div class="blur-background"></div>
      <div class="overlay-message">
        <LoadingAnimation/>
        <h3>{{$t('checkup_plus.patient_details.downloading_report')}}</h3>
      </div>
    </div>

    <!-- ----------------------------- -->
    <!-- -------Patient Details------- -->
    <!-- ----------------------------- -->
    <div v-if="patientDetails !== null && patientDetails.status === 1 && !isLoadingPatientData" ref="patientDetails">
      <va-card>
        <div class="row col-12 col-md-12 d-flex justify--end">
          <va-popover
            v-if="isFromEvollu && patientDetails && (hasPermissionToSendReportToPatient || hasPermissionToSendReportToPatientAndOthers)"
            placement="left"
            :message="patientDetails.email === null ? $t('dashboard_new_evollu.send_pdf_no_email') : $t('checkup_plus.patient_details.generate_report')">
            <va-button large icon="fa fa-file" color="#29C4AA" @click.prevent="handleGenerateReport()"
                       :disabled="isSendingPdf || patientDetails === null"></va-button>
          </va-popover>

          <va-popover
            v-if="patientDetails && (hasPermissionToSendReportToPatient || hasPermissionToSendReportToPatientAndOthers)"
            placement="top"
            :message="patientDetails.email === null ? $t('dashboard_new_evollu.send_pdf_no_email') : $t('dashboard_new_evollu.send_pdf')">
            <va-button large icon="fa fa-envelope" color="#29C4AA" @click.prevent="sendPdfByEmail()"
                       :disabled="isSendingPdf || patientDetails.email === null"></va-button>
          </va-popover>
          <va-popover
            placement="top"
            :message="$t('reports.reports.buttons.download_report')">
            <va-button large icon="fa fa-download" color="#29C4AA" @click.prevent="handlePdfDownload()"
                       :disabled="isSendingPdf || patientDetails === null"></va-button>
          </va-popover>
        </div>
        <div class="row col-12 col-md-12">
          <div class="col-md-8">
            <h5>{{ $t('dashboard_new_evollu.titles.user_details') }} </h5>
            <div class="col-12 col-md-12 row">
              <div class="col-md-6" style="margin-left: -17px !important;">
                <p>{{ $t('dashboard_new_evollu.titles.title') }}:
                  <span class="is-bold">{{
                      patientDetails.title ? patientDetails.title : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
                <p>{{ $t('dashboard_new_evollu.titles.name') }}:
                  <span class="is-bold">{{
                      patientDetails.name ? patientDetails.name : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
                <!--<p>{{ $t('dashboard_new_evollu.titles.first_name') }}:
                  <span class="is-bold">{{
                      patientDetails.firstName ? patientDetails.firstName : patientDetails.name
                    }}</span>
                </p>
                <p>{{ $t('dashboard_new_evollu.titles.last_name') }}:
                  <span class="is-bold">{{
                      patientDetails.surname ? patientDetails.surname : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>-->
                <p>{{ $t('dashboard_new_evollu.titles.birthdate') }}:
                  <span class="is-bold">{{
                      patientDetails.dateOfBirth ? patientDetails.dateOfBirth : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
                <p>Email:
                  <span class="is-bold">{{
                      patientDetails.email ? patientDetails.email : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
                <p>{{ $t('dashboard_new_evollu.titles.mobile') }}:
                  <span class="is-bold">{{
                      patientDetails.countryCallingCode ? patientDetails.countryCallingCode + ' ' : ''
                    }}
                  {{patientDetails.mobile ? patientDetails.mobile : $t('dashboard_new_evollu.values.empty')}}</span>
                </p>
                <p>{{ $t('dashboard_new_evollu.titles.username') }}:
                  <span class="is-bold">{{
                      patientDetails.username ? patientDetails.username : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
                <p>{{ $t('dashboard_new_evollu.titles.height') }}:
                  <span class="is-bold">{{
                      patientDetails.height ? patientDetails.height + " cm" : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
              </div>
              <div class="col-md-6">
                <p>{{ $t('people_manager.tax_nr') }}:
                  <span class="is-bold">{{
                      patientDetails.taxNr ? patientDetails.taxNr : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
                <p>{{ $t('people_manager.health_nr') }}:
                  <span class="is-bold">{{
                      patientDetails.healthNr ? patientDetails.healthNr : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
                <p>{{ $t('dashboard_new_evollu.titles.address') }}:
                  <span class="is-bold">{{
                      handleAddress()
                    }}</span>
                </p>
                <p v-if="patientDetails.countryAlpha2Code && patientDetails.countryAlpha2Code.toLowerCase() !== 'pt' ">{{ $t('dashboard_new_evollu.titles.zipcode') }}:
                  <span class="is-bold">{{
                      handleZipCode()
                    }}</span>
                </p>
                <p>{{ $t('dashboard_new_evollu.titles.country') }}:
                  <span class="is-bold">{{
                      patientDetails.countryAlpha2Code ? handleCountry(patientDetails.countryAlpha2Code) : $t('dashboard_new_evollu.values.empty')
                    }}</span>
                </p>
              </div>
            </div>


            <!--<div class="col-12 col-md-12 row">
              <div class="col-md-6" style="margin-left: -17px !important;">
                <p>{{ $t('dashboard_new_evollu.titles.created_at') }}: <span class="is-bold">{{
                    patientDetails.created_at
                  }}</span></p>
              </div>
              <div class="col-md-6">
              </div>
            </div>-->
          </div>
          <div class="col-md-4">
            <h5>{{ $t('dashboard_new_evollu.titles.entity_details') }} </h5>
            <!--<p>{{ $t('dashboard_new_evollu.titles.name') }}:
              <span class="is-bold">{{ entityDetails.name }}</span>
            </p>-->
            <p>{{ $t('reports.templates.table.facilities') }}:
              <ul v-if="patientDetails.facilitiesEvollu && patientDetails.facilitiesEvollu.length >0">
                <li v-for="facility in patientDetails.facilitiesEvollu">
                  <span class="is-bold" :class="{ em: !patientDetails.facilitiesEvollu }">
                •{{ facility.name }}
              </span>
                </li>
              </ul>
              <span v-else class="is-bold" :class="{ em: !patientDetails.facilitiesEvollu }">
                {{
                  $t('dashboard_new_evollu.values.empty_facilities')
                }}
              </span>
            </p>

            <div v-if="patientDetails.evolluConsentId && true && patientDetails.evolluConsentId !== ''"
                 class="col-md-12 p-0">
            <span class="clickable" @click.prevent="handlePrivacyPolicyClick">
              <span class="clickable is-bold">{{ $t('leads.details.privacy_policy') }} </span>
              <i class="px-1 fa fa-external-link"></i>
            </span>
            </div>
          </div>
        </div>
      </va-card>
    </div>
    <!-- ----------------------------- -->
    <!-- -------Patient Details------- -->
    <!-- ----------------------------- -->
    <div v-if="canSeeMeasurements" >
      <!--DETAILED DATA FROM SELECTED CARD -->
      <div v-if="selectedCardData !== null">
        <div v-if="selectedCardData && selectedCardData.data.length > 0">
          <TableAndAudiologyTest
            v-if="selectedCardData && selectedCardData.identifier === 'audiology'"
            :data="selectedCardData.data"
            :columns="columnsByMeasurement[selectedCardData.originalIdentifier]"
            :measurementTitle="$t(`dashboard_new_evollu.parameters.${selectedCardData.identifier}`)"
            :icon="`i-checkup-${selectedCardData.identifier}`"
            :lastUpdate="selectedCardData.data.length > 0 ? selectedCardData.data[0].updated_at : 'N/A'"
            @collapse="selectedCardIdentifier = null"
          />
          <Mapa24Table
            v-else-if="selectedCardData && selectedCardData.identifier === 'mapa24'"
            :data="selectedCardData.data"
            :patientDetails="patientDetails"
            :columns="columnsByMeasurement[selectedCardData.originalIdentifier]"
            :measurementTitle="$t(`dashboard_new_evollu.parameters.${selectedCardData.identifier}`)"
            :icon="`i-checkup-${selectedCardData.identifier}`"
            :lastUpdate="selectedCardData.data.length > 0 ? selectedCardData.data[0].updated_at : 'N/A'"
            @collapse="selectedCardIdentifier = null"
          />
          <QuestionnairesTable
            v-else-if="selectedCardData && selectedCardData.identifier === 'checkup_anamnese'"
            :data="selectedCardData.data"
            :patientDetails="patientDetails"
            :columns="columnsByMeasurement[selectedCardData.originalIdentifier]"
            :measurementTitle="$t(`dashboard_new_evollu.parameters.${selectedCardData.identifier}`)"
            :icon="`i-checkup-${selectedCardData.identifier}`"
            :lastUpdate="selectedCardData.data.length > 0 ? selectedCardData.data[0].updated_at : 'N/A'"
            @collapse="selectedCardIdentifier = null"
          />
          <TableAndGraph
            v-else
            :allowShowProfileModal="selectedCardData.originalIdentifier === 'frailtyPhenotype' || selectedCardData.originalIdentifier === 'sarcopenia'"
            :hideMaxAvgMin="true"
            :data="selectedCardData.data"
            :patientDetails="patientDetails"
            :columns="columnsByMeasurement[selectedCardData.originalIdentifier]"
            :graphData="selectedCardData.chartData"
            :statsData="selectedCardData.statsData"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t(`dashboard_new_evollu.parameters.${selectedCardData.identifier}`)"
            :icon="`i-checkup-${selectedCardData.identifier}`"
            :lastUpdate="selectedCardData.data.length > 0 ? selectedCardData.data[0].updated_at : 'N/A'"
            @collapse="selectedCardIdentifier = null"
          />
        </div>
        <!--<va-card ref="card-detail-view">
          <div class="d-flex justify--end">
            <i class="i-checkup-collapse-data clickable" @click.prevent="selectedCardIdentifier = null"></i>
          </div>
          <div class="selected-card-container">
            <div class="left-div">
              <MeasurementCard
                v-if="selectedCardData.data"
                :title="$t(`dashboard_new_evollu.parameters.${selectedCardData.identifier}`)"
                :lastUpdate="selectedCardData.data.length > 0 ? selectedCardData.data[0].updated_at : 'N/A'"
                :isLoading="selectedCardData.loading"
                :data="selectedCardData.data"
                :statsData="selectedCardData.statsData"
                :icon="`i-checkup-${selectedCardData.identifier}`"
                :hideTopIcon="true"
              />
            </div>
            <div class="right-div">
              <div v-if="selectedCardData && selectedCardData.data.length > 0">
                <TableAndAudiologyTestCard
                  v-if="selectedCardData && selectedCardData.identifier === 'audiology'"
                  :data="selectedCardData.data"
                  :columns="columnsByMeasurement[selectedCardData.originalIdentifier]"
                />
                <TableAndGraphCard
                  v-else
                  :hideMaxAvgMin="true"
                  :data="selectedCardData.data"
                  :columns="columnsByMeasurement[selectedCardData.originalIdentifier]"
                  :graphData="selectedCardData.chartData"
                  :statsData="selectedCardData.statsData"
                  @update_measurement="updateMeasurementTypeFromId"
                  :canDeleteMeasurement="canDeleteMeasurement"
                  :measurementTitle="$t(`dashboard_new_evollu.parameters.${selectedCardData.identifier}`)"
                />
              </div>
              <div v-else>
                <span class="pl-4">{{ $t('clinical.people.no_data') }}</span>
              </div>
            </div>
          </div>
        </va-card>-->
      </div>

      <!-- Measurements Cards -->
      <div class="row collapsed-cards justify--center">
        <div v-for="elem in measurementCardsIdentifiers">
          <MeasurementCard
            style="padding: 8px"
            v-if="elem.data"
            :title="$t(`dashboard_new_evollu.parameters.${elem.identifier}`)"
            :lastUpdate="elem.data.length > 0 ? elem.data[0].updated_at : 'N/A'"
            :isLoading="loading[elem.originalIdentifier]"
            :data="elem.data"
            :statsData="elem.statsData"
            :icon="`i-checkup-${elem.identifier}`"
            :class="{'selected-card-bg': selectedCardIdentifier && selectedCardIdentifier === elem.originalIdentifier}"
            @cardClicked="handleMeasurementCardExpand(elem.originalIdentifier)"
            @closeCardUpView="selectedCardIdentifier = null"
            :isSelected="selectedCardIdentifier && selectedCardIdentifier === elem.originalIdentifier"
            :canCreateMeasurement="canCreateMeasurement"
            :hideAddBtn="elem.originalIdentifier === 'audiology' || elem.originalIdentifier === 'sarcopenia' || elem.originalIdentifier === 'frailtyPhenotype' || elem.originalIdentifier === 'optometry' || elem.originalIdentifier === 'checkupAnamnesis'"
            :patientDetails="patientDetails"
            @handleCreateMeasurement="handleCreateMeasurement(elem.measurementTypeId)"
          />
        </div>
      </div>
      <!-- ------------------------------------------------->

      <!--<CollapsableCardWithBody
        :title="$t('dashboard_new_evollu.parameters.audiology')"
        :lastUpdate="audiologyData.length > 0 ? audiologyData[0].updated_at : 'N/A'"
        :isLoading="loading.audiology">
        <TableAndAudiologyTestCard
          v-if="audiologyData.length > 0"
          :data="audiologyData"
          :columns="['created_at', 'hl_left', 'hl_right', 'hl', 'automatic']"
        />
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>-->

      <!--<CollapsableCardWithBody
        v-if="bloodPressureData"
        :title="$t('dashboard_new_evollu.parameters.blood_pressure')"
        :lastUpdate="bloodPressureData.length > 0 ? bloodPressureData[0].updated_at : 'N/A'"
        :isLoading="loading.bloodPressure">
        <div v-if="bloodPressureData.length > 0">
          <TableAndGraphCard
            :data="bloodPressureData"
            :columns="['created_at', 'systolic', 'diastolic', 'heart_rate', 'automatic', 'notes']"
            :graphData="bloodPressureChartData"
            :statsData="bloodPressureStatsData"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.blood_pressure')"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>

      <CollapsableCardWithBody
        v-if="oximetryData"
        :title="$t('dashboard_new_evollu.parameters.oximetry')"
        :lastUpdate="oximetryData.length > 0 ? oximetryData[0].updated_at : 'N/A'"
        :isLoading="loading.oximetry">
        <div v-if="oximetryData.length > 0">
          <TableAndGraphCard
            :data="oximetryData"
            :columns="['created_at', 'duration_session', 'oximetry_max', 'oximetry_min', 'oximetry_avg', 'pulse_rate_max', 'pulse_rate_min', 'pulse_rate_avg', 'automatic', 'notes']"
            :graphData="oximetryChartData"
            :stats-data="oximetryStatsData"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.oximetry')"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>

      <CollapsableCardWithBody
        v-if="glucoseData"
        :title="$t('dashboard_new_evollu.parameters.blood_glucose')"
        :lastUpdate="glucoseData.length > 0 ? glucoseData[0].updated_at : 'N/A'"
        :isLoading="loading.glucose">
        <div v-if="glucoseData.length > 0">
          <TableAndGraphCard
            :data="glucoseData"
            :columns="['created_at', 'blood_glucose', 'fasting', 'automatic']"
            :graphData="glucoseChartData"
            :stats-data="glucoseStatsData"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.blood_glucose')"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>

      <CollapsableCardWithBody
        v-if="spirometryData"
        :title="$t('dashboard_new_evollu.parameters.spirometry')"
        :lastUpdate="spirometryData.length > 0 ? spirometryData[0].updated_at : 'N/A'"
        :isLoading="loading.spirometry">
        <div v-if="spirometryData.length > 0">
          <TableAndGraphCard
            :data="spirometryData"
            :columns="['created_at', 'forced_expiratory_volume_6_sec', 'forced_expiratory_volume_1_sec', 'fev1_fev6', 'forced_mid_expiratory_flow_25_75', 'automatic']"
            :graphData="spirometryChartData"
            :stats-data="spirometryStatsData"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.spirometry')"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>

      <CollapsableCardWithBody
        v-if="temperatureData"
        :title="$t('dashboard_new_evollu.parameters.temperature')"
        :lastUpdate="temperatureData.length > 0 ? temperatureData[0].updated_at : 'N/A'"
        :isLoading="loading.temperature">
        <div v-if="temperatureData.length > 0">
          <TableAndGraphCard
            :data="temperatureData"
            :columns="['created_at', 'body_temperature', 'automatic']"
            :graphData="temperatureChartData"
            :statsData="temperatureStatsData"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.temperature')"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>

      <CollapsableCardWithBody
        v-if="bodyCompositionData"
        :title="$t('dashboard_new_evollu.parameters.body_composition')"
        :lastUpdate="bodyCompositionData.length > 0 ? bodyCompositionData[0].updated_at : 'N/A'"
        :isLoading="loading.bodyComposition">
        <div v-if="bodyCompositionData.length > 0">
          <TableAndGraphCard
            :data="bodyCompositionData"
            :columns="['created_at', 'body_weight', 'body_mass_index', 'body_water_percentage', 'body_fat_percentage', 'body_muscle_mass', 'automatic']"
            :graphData="bodyCompositionChartData"
            :statsData="bodyCompositionStatsData"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.body_composition')"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>

      <CollapsableCardWithBody
        v-if="sarcopeniaData"
        :title="$t('dashboard_new_evollu.parameters.sarcopenia')"
        :lastUpdate="sarcopeniaData.length > 0 ? sarcopeniaData[0].updated_at : 'N/A'"
        :isLoading="loading.sarcopenia">
        <div v-if="sarcopeniaData.length > 0">
          <TableAndGraphCard
            :data="sarcopeniaData"
            :columns="['created_at', 'sarc_f', 'hand_grip', 'body_muscle_mass', 'gait_speed', 'sarc_result', 'automatic']"
            :graphData="sarcopeniaChartData"
            :statsData="sarcopeniaStatsData"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.sarcopenia')"
            :patientDetails="patientDetails"
            :allowShowProfileModal="true"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>

      <CollapsableCardWithBody
        v-if="frailtyPhenotypeData"
        :title="$t('dashboard_new_evollu.parameters.frailty_phenotype')"
        :lastUpdate="frailtyPhenotypeData.length > 0 ? frailtyPhenotypeData[0].updated_at : 'N/A'"
        :isLoading="loading.frailtyPhenotype">
        <div v-if="frailtyPhenotypeData.length > 0">
          <TableAndGraphCard
            :data="frailtyPhenotypeData"
            :columns="['created_at', 'frailty_accessment_result', 'frailty_exhaustion_result', 'frailty_physical_activity', 'frailty_walk_time', 'frailty_dominant_strength', 'frailty_weight_loss', 'automatic']"
            :graphData="frailtyPhenotypeChartData"
            :statsData="frailtyPhenotypeStatsData"
            :hideMaxAvgMin="true"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.frailty_phenotype')"
            :patientDetails="patientDetails"
            :allowShowProfileModal="true"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody>

      <CollapsableCardWithBody
        v-if="cholesterolData"
        :title="$t('dashboard_new_evollu.parameters.cholesterol')"
        :lastUpdate="cholesterolData.length > 0 ? cholesterolData[0].updated_at : 'N/A'"
        :isLoading="loading.cholesterol">
        <div v-if="cholesterolData.length > 0">
          <TableAndGraphCard
            :data="cholesterolData"
            :columns="['created_at', 'cholesterol_total', 'hdl_cholesterol', 'ldl_cholesterol', 'triglycerides', 'automatic']"
            :graphData="cholesterolChartData"
            :statsData="cholesterolStatsData"
            :hideMaxAvgMin="false"
            @update_measurement="updateMeasurementTypeFromId"
            :canDeleteMeasurement="canDeleteMeasurement"
            :measurementTitle="$t('dashboard_new_evollu.parameters.cholesterol')"
          />
        </div>
        <div v-else>
          <span>{{ $t('clinical.people.no_data') }}</span>
        </div>
      </CollapsableCardWithBody> -->

      <div>
        <va-separator style="height: 8px; border-radius: 25px"/>
        <div>
          <RulesList
            :limitRules="patientDetails.limitRules"
            :person="patientDetails"
            @ruleDeleted="ruleDeleted"
          />
        </div>
      </div>

      <div v-if="canCreateMeasurement && !isLoading">
        <div class="btn btn__floating btn__round__large">
          <va-popover
            :message="$t('checkup_plus.person.measurements.new_measurement')"
            placement="top"
          >
            <va-button
              @click.prevent="showCreateMeasurementModal = true">
              <i class="fa fa-plus"></i>
            </va-button>

          </va-popover>
        </div>
      </div>


    </div>

    <CreateMeasurementModal
      v-if="canCreateMeasurement && showCreateMeasurementModal"
      :patientDetails="patientDetails"
      @cancel="onCancelCreateMeasurementModal"
      @measurementCreated="handleMeasuremenCreated"
      :selectedMeasurementId="measurementIdToCreate"
    />
  </div>

</template>

<script>
import Breadcrumbs from "@/components/common/Breadcrumbs";
import AudiologyDetails from "@/components/checkup_plus/patients/ParametersDetails/CollapsableCardWithBody";
import CollapsableCardWithBody from "@/components/checkup_plus/patients/ParametersDetails/CollapsableCardWithBody";
import HearingTest from "@/components/common/HearingTest";
import moment from "moment";
import TableAndGraphCard from "@/components/checkup_plus/patients/ParametersDetails/TableAndGraphCard";
import {OneCareService} from "@/api/OneCareService";
import LoadingAnimation from "@/components/users/components/Helpers/LoadingAnimation";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import UpdatePatient from "@/components/checkup_plus/patients/UpdatePatient";
import TableAndAudiologyTestCard from "@/components/checkup_plus/patients/ParametersDetails/TableAndAudiologyTestCard";
import RulesList from "@/components/checkup_plus/patients/rules/RulesList.vue";
import MeasurementCard from "@/components/checkup_plus/patients/ParametersDetails/MeasurementCard.vue";
import TableAndGraph from "@/components/checkup_plus/patients/ParametersDetails/TableAndGraph.vue";
import TableAndAudiologyTest from "@/components/checkup_plus/patients/ParametersDetails/TableAndAudiologyTest.vue";
import Mapa24Table from "@/components/checkup_plus/patients/ParametersDetails/Mapa24/Table.vue";
import FadeTransition from "@/components/common/transitions/FadeTransition.vue";
import CreateMeasurementModal from "@/components/checkup_plus/patients/ParametersDetails/CreateMeasurementModal.vue"
import QuestionnairesTable
  from '@/components/checkup_plus/patients/ParametersDetails/Questionnaires/QuestionnairesTable.vue';
import * as countries from 'i18n-iso-countries'
import Vue from 'vue';
import IsFromEvolluMixin from '@/mixins/IsFromEvolluMixin.vue';
const BLOOD_PRESSURE_MEASUREMENT_TYPE_ID = 2
const TEMPERATURE_MEASUREMENT_TYPE_ID = 3
const GLUCOSE_MEASUREMENT_TYPE_ID = 4
const BODY_COMPOSITION_MEASUREMENT_TYPE_ID = 5
const SPIROMETRY_MEASUREMENT_TYPE_ID = 11
const OXIMETRY_MEASUREMENT_TYPE_ID = 18
const MAPA_24_MEASUREMENT_TYPE_ID = 20
const FRAILTY_PHENOTYPE_TYPE_ID = 6000
const SARCOPENIA_MEASUREMENT_TYPE_ID = 6001
const TONAL_TEST_MEASUREMENT_TYPE_ID = 9000
const OPTOMETRY_MEASUREMENT_TYPE_ID = 9050
const CHOLESTEROL_MEASUREMENT_TYPE_ID = 10000
const BODY_CIRCUMFERENCE_MEASUREMENT_TYPE_ID = 21
const CHECKUP_ANAMNESE_MEASUREMENT_TYPE_ID = 1014


const PEOPLE_MANAGER_LIST_INFO_TYPE = 110

export default {
  name: "PatientDetails",
  mixins: [IsFromEvolluMixin],
  components: {
    QuestionnairesTable,
    FadeTransition,
    TableAndAudiologyTest,
    MeasurementCard,
    RulesList,
    TableAndAudiologyTestCard,
    LoadingAnimation,
    TableAndGraphCard,
    CollapsableCardWithBody,
    HearingTest,
    AudiologyDetails,
    Breadcrumbs,
    ConfirmationModal,
    UpdatePatient,
    TableAndGraph,
    Mapa24Table,
    CreateMeasurementModal
  },
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
  },
  async created() {
    this.error = null
    await this.loadData(true)
  },
  data: function () {
    return {
      measurementsTypesIds: [
        BLOOD_PRESSURE_MEASUREMENT_TYPE_ID,
        OXIMETRY_MEASUREMENT_TYPE_ID,
        SPIROMETRY_MEASUREMENT_TYPE_ID,
        TEMPERATURE_MEASUREMENT_TYPE_ID,
        BODY_COMPOSITION_MEASUREMENT_TYPE_ID,
        SARCOPENIA_MEASUREMENT_TYPE_ID,
        FRAILTY_PHENOTYPE_TYPE_ID,
        GLUCOSE_MEASUREMENT_TYPE_ID,
        CHOLESTEROL_MEASUREMENT_TYPE_ID,
        MAPA_24_MEASUREMENT_TYPE_ID,
        OPTOMETRY_MEASUREMENT_TYPE_ID,
        BODY_CIRCUMFERENCE_MEASUREMENT_TYPE_ID,
        CHECKUP_ANAMNESE_MEASUREMENT_TYPE_ID
      ],
      isLoading: true,
      isLoadingPatientData: true,
      measurementsData: null,
      patientDetails: null,
      entityDetails: {
        name: "",
        facility: ""
      },
      hearingTestData: null,
      audiologyData: [],
      audiologyStatsData: {},
      bloodPressureData: [],
      bloodPressureStatsData: {},
      oximetryData: [],
      oximetryStatsData: {},
      spirometryData: [],
      spirometryStatsData: {},
      temperatureData: [],
      temperatureStatsData: {},
      bodyCompositionData: [],
      bodyCompositionStatsData: {},
      sarcopeniaData: [],
      sarcopeniaStatsData: {},
      frailtyPhenotypeData: [],
      frailtyPhenotypeStatsData: {},
      glucoseData: [],
      glucoseStatsData: {},
      cholesterolData: [],
      cholesterolStatsData: {},
      mapa24Data: [],
      mapa24StatsData: {},
      optometryData: [],
      optometryStatsData: {},
      bodyCircumferenceData: [],
      bodyCircumferenceStatsData: {},
      isSendingPdf: false,
      canBlockOrDeleteUser: true,
      showStatusChangePatientModal: false,
      showDeletePatient: false,
      showUpdatePatient: false,
      downloadPdfReportLoading: false,
      loading: {
        audiology: true,
        bloodPressure: true,
        oximetry: true,
        spirometry: true,
        temperature: true,
        bodyComposition: true,
        sarcopenia: true,
        frailtyPhenotype: true,
        glucose: true,
        cholesterol: true,
        mapa24: true,
        optometry: true,
        bodyCircumference: true,
        checkupAnamnese: true
      },
      columnsByMeasurement: {
        audiology: ['created_at', 'hl_left', 'hl_right', 'hl', 'automatic'],
        bloodPressure: ['created_at', 'systolic', 'diastolic', 'heart_rate', 'automatic', 'notes'],
        oximetry: ['created_at', 'duration_session', 'oximetry_max', 'oximetry_min', 'oximetry_avg', 'pulse_rate_max', 'pulse_rate_min', 'pulse_rate_avg', 'automatic', 'notes'],
        glucose: ['created_at', 'blood_glucose', 'fasting', 'automatic'],
        spirometry: ['created_at', 'forced_expiratory_volume_6_sec', 'forced_expiratory_volume_1_sec', 'fev1_fev6', 'forced_mid_expiratory_flow_25_75', 'automatic'],
        temperature: ['created_at', 'body_temperature', 'automatic'],
        bodyComposition: ['created_at', 'body_weight', 'body_mass_index', 'body_water_percentage', 'body_fat_percentage', 'body_muscle_mass', 'automatic'],
        sarcopenia: ['created_at', 'sarc_f', 'hand_grip', 'body_muscle_mass', 'gait_speed', 'sarc_result', 'automatic'],
        frailtyPhenotype: ['created_at', 'frailty_accessment_result', 'frailty_exhaustion_result', 'frailty_physical_activity', 'frailty_walk_time', 'frailty_dominant_strength', 'frailty_weight_loss', 'automatic'],
        cholesterol: ['created_at', 'cholesterol_total', 'hdl_cholesterol', 'ldl_cholesterol', 'triglycerides', 'automatic'],
        mapa24: ['created_at', 'measurement_type', 'automatic'],
        optometry: ['created_at', 'optometry_result', 'astigmatism', 'duochrome', 'chromatic_correct', 'contrast', 'macular', 'acuity'],
        bodyCircumference : ['created_at', 'waist_perimeter', 'abdomen_perimeter', 'chest_perimeter', 'relaxed_arm_perimeter', 'thigh_perimeter', 'calf_perimeter'],
        checkupAnamnese: ['created_at','measurement_type', 'automatic']
      },
      selectedCardIdentifier: null,
      showCreateMeasurementModal: false,
      measurementIdToCreate: null,
      checkupAnamneseData: [],
      checkupAnamneseStatsData: {}
    }
  },
  methods: {
    handlePdfDownload: function () {
      //Start downloadPdfLoading
      this.downloadPdfReportLoading = true;
      OneCareService.downloadReportPdf(this.patientDetails.idPerson)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `Relatório_${this.handlePatientName}.pdf`)
          document.body.appendChild(link)
          this.downloadPdfReportLoading = false;
          link.click()
        })
        .catch(errors => {
          this.downloadPdfReportLoading = false;
          this.showToast(this.$t('reports.templates.messages.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    },
    handleUpdatePatientPm() {
      if(!this.patientDetails.peopleManagerId || !this.peopleManagerService) return
      this.$router.push({ name: `${this.peopleManagerService.slug}/people-manager/list/patient/id`, params: { id: this.patientDetails.peopleManagerId, showEdit: true } })
    },
    handleCountry(isoCountryCode) {
      const country = this.countries.find(country => country.code.toLowerCase() === isoCountryCode.toLowerCase() )
      return country.name
    },
    handleGenerateReport() {
      this.$router.push({name: `check-up_plus/checkup_plus/patients/id/report`, params: {id: this.patientDetails.idPerson}})
    },
    handleMeasurementCardExpand(identifier) {
      this.selectedCardIdentifier = identifier
      this.scrollToOverviewCard()
    },
    scrollToOverviewCard() {
      var element = this.$refs["patientDetails"];
      element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" })
    },
    handleCreateMeasurement(measurementTypeId) {
      this.measurementIdToCreate = measurementTypeId
      this.showCreateMeasurementModal = true
    },
    async handleMeasuremenCreated(measurementData, identifier) {
      this.loading[identifier] = true
      this.isLoading = true
      await this.getMeasurementDataFromTypeId(measurementData.measurementGroup.idMeasurementGroup)
      this.isLoading = false
      this.loading[identifier] = false
      this.measurementIdToCreate = null
      this.showCreateMeasurementModal = false
    },
    onCancelCreateMeasurementModal() {
      this.showCreateMeasurementModal = false;
      this.measurementIdToCreate = null
    },
    ruleDeleted: function (rule) {
      const idx = this.patientDetails.limitRules.findIndex(elem => elem.limitRuleId === rule.limitRuleId)
      if (idx !== null && idx !== undefined) {
        this.patientDetails.limitRules.splice(idx, 1)
      }
    },
    handlePrivacyPolicyClick: function () {
      window.open(`${process.env.VUE_APP_CONSENT_URL}v1/consent/${this.patientDetails.evolluConsentId}`);
    },
    handleAddress: function() {
      let addressStr = ''
      if (this.patientDetails && this.patientDetails.address) {
        addressStr += this.patientDetails.address + " "
      }
      if(this.patientDetails.countryAlpha2Code.toLowerCase() === 'pt') {
        if(this.patientDetails.municipio) {
          addressStr += ", "+ this.patientDetails.municipio.name
        }
        if(this.patientDetails.municipio && this.patientDetails.municipio.districtName && this.patientDetails.municipio.districtName !== '') {
          addressStr += ', '+this.patientDetails.municipio.districtName + ' '
        } else {
          addressStr +=' '
        }
      } else {
        if(this.patientDetails && this.patientDetails.county) {
          addressStr += ", " + this.patientDetails.county + " "
        }
      }

      if (this.patientDetails.zipPrefix && this.patientDetails.zipPrefix !== '') {
        addressStr += ", "+this.patientDetails.zipPrefix
      }
      if (this.patientDetails.zipSufix && this.patientDetails.zipSufix !== '') {
        addressStr += `-${this.patientDetails.zipSufix}`
      }

      return addressStr === '' ? "N/A" : addressStr
    },
    handleZipCode: function () {
      let zipCode = ''
      if (this.patientDetails.zipPrefix && this.patientDetails.zipPrefix !== '') {
        zipCode += this.patientDetails.zipPrefix
      }
      if (this.patientDetails.zipSufix && this.patientDetails.zipSufix !== '') {
        zipCode += `-${this.patientDetails.zipSufix}`
      }
      return zipCode !== '' ? zipCode : "N/A"
      //patientDetails.zipPrefix ? `${patientDetails.zipPrefix}-${patientDetails.zipSufix}` : $t('dashboard_new_evollu.values.empty')
    },
    loadData: async function (fetchMeasurements) {
      this.isLoading = true
      this.isLoadingPatientData = true

      try {
        this.patientDetails = await OneCareService.getPatient(this.$route.params.id)
        this.patientDetails.dateOfBirth = moment(this.patientDetails.dateOfBirth).format('YYYY-MM-DD')
        if (this.patientDetails.municipio === null) {
          this.patientDetails.municipio = {}
        }
        this.isLoadingPatientData = false
      } catch (e) {
        console.log("Error fetching patient data: " + e.message)
      }

      //handle tonalTestData

      if (fetchMeasurements && this.canSeeMeasurements) {
        const audiologyData = await OneCareService.getPatientMeasurements(this.$route.params.id, TONAL_TEST_MEASUREMENT_TYPE_ID)
        this.parseTonalTestData(audiologyData.objects)

        for (const id of this.measurementsTypesIds) {
          try {
            await this.getMeasurementDataFromTypeId(id)
          } catch (e) {
            console.error(`Request for measurement with id ${id} has errored`)
            console.log(e)
          }
        }
      }
      this.isLoading = false
    },
    sendPdfByEmail: async function () {
      try {
        this.isSendingPdf = true
        await OneCareService.sendPdfByEmail(this.patientDetails.idPerson, this.patientDetails.email)
        this.showToast(this.$t('dashboard_new_evollu.pdf_sent_successfully'), this.$store.state.successToastOptions)
        this.isSendingPdf = false
      } catch (e) {
        this.isSendingPdf = false
        this.showToast(this.$t('dashboard_new_evollu.pdf_not_sent'), this.$store.state.errorToastOptions)
      }
    },
    parseTonalTestData: function (data) {
      const HEAR_LOSS_LEFT_TYPE_ID = 9009
      const HEAR_LOSS_RIGHT_TYPE_ID = 9010
      const HEAR_LOSS_RESULT_TYPE_ID = 9011

      this.audiologyData = []
      const frequenciesToTypeIdMapping = {
        9001: {side: 'left', freq: 1000},
        9002: {side: 'left', freq: 2000},
        9003: {side: 'left', freq: 4000},
        9004: {side: 'left', freq: 6000},
        9005: {side: 'right', freq: 1000},
        9006: {side: 'right', freq: 2000},
        9007: {side: 'right', freq: 4000},
        9008: {side: 'right', freq: 6000}
      }

      let lastChartResultStr = "N/A"

      if(data.length) {
        const lastChartResults = data[0].chartResults
        if(lastChartResults.length > 0) {
          lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
        }
      }

      data.forEach(elem => {
        let results = {
          left: {},
          right: {}
        }
        elem.measurements.forEach(elem => {
          if (frequenciesToTypeIdMapping[elem.typeId]) {
            const side = frequenciesToTypeIdMapping[elem.typeId].side
            const freq = frequenciesToTypeIdMapping[elem.typeId].freq
            if (side && freq) {
              results[side][freq] = elem.value
            }
          }
        })

        let resultStr = `F\t1000\t2000\t4000\t6000\nR\t${results.right['1000']}\t${results.right['2000']}\t${results.right['4000']}\t${results.right['6000']}\nL\t${results.left['1000']}\t${results.left['2000']}\t${results.left['4000']}\t${results.left['6000']}`
        this.audiologyData.push({
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          hl_left: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === HEAR_LOSS_LEFT_TYPE_ID)[0], ''),
          hl_right: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === HEAR_LOSS_RIGHT_TYPE_ID)[0], ''),
          hl: this.parseBoolean(this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === HEAR_LOSS_RESULT_TYPE_ID)[0], '')),
          hl_raw: parseInt(this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === HEAR_LOSS_RESULT_TYPE_ID)[0], '')),
          result_graph_str: resultStr,
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr
        })

        if(data.length > 0) {
          let audiologyStatsData = {
            'hl_left': {},
            'hl_right': {},
            'hl': {}
          }

          audiologyStatsData.hl_left['last'] = this.checkStringValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === HEAR_LOSS_LEFT_TYPE_ID), 'dB')
          audiologyStatsData.hl_right['last'] = this.checkStringValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === HEAR_LOSS_RIGHT_TYPE_ID), 'dB')
          audiologyStatsData.hl['last'] = this.parseBoolean(this.checkStringValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === HEAR_LOSS_RESULT_TYPE_ID), ''))
          this.audiologyStatsData = {...audiologyStatsData}
        }
      })
      this.loading.audiology = false
    },
    parseBloodPressureData: function (data) {
      const SYSTOLIC_TYPE_ID = 2
      const DIASTOLIC_TYPE_ID = 3
      const HEART_RATE_TYPE_ID = 4
      this.bloodPressureData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }

      data.forEach(elem => {
        this.bloodPressureData.push({
          id: elem.idMeasurement,
          measurementTypeId: BLOOD_PRESSURE_MEASUREMENT_TYPE_ID,
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          systolic: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === SYSTOLIC_TYPE_ID)[0], 'mmHg'),
          diastolic: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === DIASTOLIC_TYPE_ID)[0], 'mmHg'),
          heart_rate: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === HEART_RATE_TYPE_ID)[0], 'bpm'),
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr,
          notes: null,
        })
      })
      if (data.length > 0) {
        let bloodPressureStatsData = {
          'systolic': {},
          'diastolic': {},
          'heart_rate': {}
        }
        bloodPressureStatsData.systolic['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SYSTOLIC_TYPE_ID)[0], 'maxValue', 'mmHg')
        bloodPressureStatsData.systolic['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SYSTOLIC_TYPE_ID)[0], 'minValue', 'mmHg')
        bloodPressureStatsData.systolic['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SYSTOLIC_TYPE_ID)[0], 'avgValue', 'mmHg')

        bloodPressureStatsData.diastolic['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === DIASTOLIC_TYPE_ID)[0], 'maxValue', 'mmHg')
        bloodPressureStatsData.diastolic['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === DIASTOLIC_TYPE_ID)[0], 'minValue', 'mmHg')
        bloodPressureStatsData.diastolic['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === DIASTOLIC_TYPE_ID)[0], 'avgValue', 'mmHg')

        bloodPressureStatsData.heart_rate['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HEART_RATE_TYPE_ID)[0], 'maxValue', 'bpm')
        bloodPressureStatsData.heart_rate['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HEART_RATE_TYPE_ID)[0], 'minValue', 'bpm')
        bloodPressureStatsData.heart_rate['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HEART_RATE_TYPE_ID)[0], 'avgValue', 'bpm')

        bloodPressureStatsData.systolic['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === SYSTOLIC_TYPE_ID), 'value', 'mmHg')
        bloodPressureStatsData.diastolic['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === DIASTOLIC_TYPE_ID), 'value', 'mmHg')
        bloodPressureStatsData.heart_rate['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === HEART_RATE_TYPE_ID), 'value', 'bpm')

        this.bloodPressureStatsData = {...bloodPressureStatsData}
      }

      this.loading.bloodPressure = false
    },
    parseOximetryData: function (data) {
      const DURATION_MONITORING_SESSION_TYPE_ID = 46
      const OXIMETRY_MAX_TYPE_ID = 47
      const OXIMETRY_MIN_TYPE_ID = 48
      const OXIMETRY_AVG_TYPE_ID = 49
      const PULSE_RATE_MAX_TYPE_ID = 50
      const PULSE_RATE_MIN_TYPE_ID = 51
      const PULSE_RATE_AVG_TYPE_ID = 52
      this.oximetryData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }

      data.forEach(elem => {
        this.oximetryData.push({
          id: elem.idMeasurement,
          measurementTypeId: OXIMETRY_MEASUREMENT_TYPE_ID,
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          duration_session: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === DURATION_MONITORING_SESSION_TYPE_ID)[0], 'min'),
          oximetry_max: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === OXIMETRY_MAX_TYPE_ID)[0], '%'),
          oximetry_min: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === OXIMETRY_MIN_TYPE_ID)[0], '%'),
          oximetry_avg: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === OXIMETRY_AVG_TYPE_ID)[0], '%'),
          pulse_rate_max: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === PULSE_RATE_MAX_TYPE_ID)[0], 'bpm'),
          pulse_rate_min: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === PULSE_RATE_MIN_TYPE_ID)[0], 'bpm'),
          pulse_rate_avg: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === PULSE_RATE_AVG_TYPE_ID)[0], 'bpm'),
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr,
          notes: null,
        })
      })
      if (data.length > 0) {
        let oximetryStatsData = {
          'duration_session': {},
          //'oximetry_max': {},
          //'oximetry_min': {},
          'oximetry_avg': {},
          //'pulse_rate_max': {},
          //'pulse_rate_min': {},
          'pulse_rate_avg': {}

        }
        oximetryStatsData.duration_session['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === DURATION_MONITORING_SESSION_TYPE_ID)[0], 'maxValue', 'min')
        oximetryStatsData.duration_session['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === DURATION_MONITORING_SESSION_TYPE_ID)[0], 'minValue', 'min')
        oximetryStatsData.duration_session['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === DURATION_MONITORING_SESSION_TYPE_ID)[0], 'avgValue', 'min')

        //oximetryStatsData.oximetry_max['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_MAX_TYPE_ID)[0], 'maxValue', '%')
        //oximetryStatsData.oximetry_max['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_MAX_TYPE_ID)[0], 'minValue', '%')
        //oximetryStatsData.oximetry_max['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_MAX_TYPE_ID)[0], 'avgValue', '%')

        //oximetryStatsData.oximetry_min['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_MIN_TYPE_ID)[0], 'maxValue', '%')
        //oximetryStatsData.oximetry_min['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_MIN_TYPE_ID)[0], 'minValue', '%')
        //oximetryStatsData.oximetry_min['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_MIN_TYPE_ID)[0], 'avgValue', '%')

        oximetryStatsData.oximetry_avg['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_AVG_TYPE_ID)[0], 'maxValue', '%')
        oximetryStatsData.oximetry_avg['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_AVG_TYPE_ID)[0], 'minValue', '%')
        oximetryStatsData.oximetry_avg['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === OXIMETRY_AVG_TYPE_ID)[0], 'avgValue', '%')

        //oximetryStatsData.pulse_rate_max['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_MAX_TYPE_ID)[0], 'maxValue', 'bpm')
        //oximetryStatsData.pulse_rate_max['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_MAX_TYPE_ID)[0], 'minValue', 'bpm')
        //oximetryStatsData.pulse_rate_max['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_MAX_TYPE_ID)[0], 'avgValue', 'bpm')

        //oximetryStatsData.pulse_rate_min['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_MIN_TYPE_ID)[0], 'maxValue', 'bpm')
        //oximetryStatsData.pulse_rate_min['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_MIN_TYPE_ID)[0], 'minValue', 'bpm')
        //oximetryStatsData.pulse_rate_min['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_MIN_TYPE_ID)[0], 'avgValue', 'bpm')

        oximetryStatsData.pulse_rate_avg['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_AVG_TYPE_ID)[0], 'maxValue', 'bpm')
        oximetryStatsData.pulse_rate_avg['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_AVG_TYPE_ID)[0], 'minValue', 'bpm')
        oximetryStatsData.pulse_rate_avg['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === PULSE_RATE_AVG_TYPE_ID)[0], 'avgValue', 'bpm')


        oximetryStatsData.duration_session['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === DURATION_MONITORING_SESSION_TYPE_ID), 'value', 'min')
        //oximetryStatsData.oximetry_max['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === OXIMETRY_MAX_TYPE_ID), 'value', '%')
        //oximetryStatsData.oximetry_min['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === OXIMETRY_MIN_TYPE_ID), 'value', '%')
        oximetryStatsData.oximetry_avg['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === OXIMETRY_AVG_TYPE_ID), 'value', '%')
        //oximetryStatsData.pulse_rate_max['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === PULSE_RATE_MAX_TYPE_ID), 'value', 'bpm')
        //oximetryStatsData.pulse_rate_min['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === PULSE_RATE_MIN_TYPE_ID), 'value', 'bpm')
        oximetryStatsData.pulse_rate_avg['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === PULSE_RATE_AVG_TYPE_ID), 'value', 'bpm')

        this.oximetryStatsData = {...oximetryStatsData}
      }
      this.loading.oximetry = false
    },
    parseSpirometryData: function (data) {
      const FORCED_EXPIRATORY_VOLUME_6_TYPE_ID = 21
      const FORCED_EXPIRATORY_VOLUME_1_TYPE_ID = 22
      const FEV1_FEV6_TYPE_ID = 23
      const FORCED_MID_EXPIRATORY_FLOW = 57
      this.spirometryData = []

      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }

      data.forEach(elem => {
        this.spirometryData.push({
          id: elem.idMeasurement,
          measurementTypeId: SPIROMETRY_MEASUREMENT_TYPE_ID,
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          forced_expiratory_volume_6_sec: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem !== null && elem.typeId === FORCED_EXPIRATORY_VOLUME_6_TYPE_ID)[0], 'L'),
          forced_expiratory_volume_1_sec: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem !== null && elem.typeId === FORCED_EXPIRATORY_VOLUME_1_TYPE_ID)[0], 'L'),
          fev1_fev6: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem !== null && elem.typeId === FEV1_FEV6_TYPE_ID)[0], ''),
          forced_mid_expiratory_flow_25_75: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem !== null && elem.typeId === FORCED_MID_EXPIRATORY_FLOW)[0], 'L/s'),
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr,
          notes: null,
        })
      })

      if (data.length > 0) {
        let spirometryStatsData = {
          'forced_expiratory_volume_6_sec': {},
          'forced_expiratory_volume_1_sec': {},
          'fev1_fev6': {},
          'forced_mid_expiratory_flow_25_75': {}
        }
        spirometryStatsData.forced_expiratory_volume_6_sec['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_EXPIRATORY_VOLUME_6_TYPE_ID)[0], 'maxValue', 'L')
        spirometryStatsData.forced_expiratory_volume_6_sec['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_EXPIRATORY_VOLUME_6_TYPE_ID)[0], 'minValue', 'L')
        spirometryStatsData.forced_expiratory_volume_6_sec['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_EXPIRATORY_VOLUME_6_TYPE_ID)[0], 'avgValue', 'L')

        spirometryStatsData.forced_expiratory_volume_1_sec['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_EXPIRATORY_VOLUME_1_TYPE_ID)[0], 'maxValue', 'L')
        spirometryStatsData.forced_expiratory_volume_1_sec['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_EXPIRATORY_VOLUME_1_TYPE_ID)[0], 'minValue', 'L')
        spirometryStatsData.forced_expiratory_volume_1_sec['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_EXPIRATORY_VOLUME_1_TYPE_ID)[0], 'avgValue', 'L')

        spirometryStatsData.fev1_fev6['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FEV1_FEV6_TYPE_ID)[0], 'maxValue', '')
        spirometryStatsData.fev1_fev6['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FEV1_FEV6_TYPE_ID)[0], 'minValue', '')
        spirometryStatsData.fev1_fev6['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FEV1_FEV6_TYPE_ID)[0], 'avgValue', '')

        spirometryStatsData.forced_mid_expiratory_flow_25_75['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_MID_EXPIRATORY_FLOW)[0], 'maxValue', 'L/s')
        spirometryStatsData.forced_mid_expiratory_flow_25_75['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_MID_EXPIRATORY_FLOW)[0], 'minValue', 'L/s')
        spirometryStatsData.forced_mid_expiratory_flow_25_75['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FORCED_MID_EXPIRATORY_FLOW)[0], 'avgValue', 'L/s')

        spirometryStatsData.forced_expiratory_volume_6_sec['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FORCED_EXPIRATORY_VOLUME_6_TYPE_ID), 'value', 'L')
        spirometryStatsData.forced_expiratory_volume_1_sec['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FORCED_EXPIRATORY_VOLUME_1_TYPE_ID), 'value', 'L')
        spirometryStatsData.fev1_fev6['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FEV1_FEV6_TYPE_ID), 'value', '')
        spirometryStatsData.forced_mid_expiratory_flow_25_75['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FORCED_MID_EXPIRATORY_FLOW), 'value', 'L/s')
        this.spirometryStatsData = {...spirometryStatsData}
      }
      this.loading.spirometry = false
    },
    parseTemperatureData: function (data) {
      const BODY_TEMPERATURE_TYPE_ID = 11

      this.temperatureData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }

      data.forEach(elem => {
        this.temperatureData.push({
          id: elem.idMeasurement,
          measurementTypeId: TEMPERATURE_MEASUREMENT_TYPE_ID,
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          body_temperature: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === BODY_TEMPERATURE_TYPE_ID)[0], 'ºC'),
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr,
          notes: null
        })
      })
      if (data.length > 0) {
        let temperatureStatsData = {
          'body_temperature': {},
        }
        temperatureStatsData.body_temperature['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_TEMPERATURE_TYPE_ID)[0], 'maxValue', 'ºC')
        temperatureStatsData.body_temperature['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_TEMPERATURE_TYPE_ID)[0], 'minValue', 'ºC')
        temperatureStatsData.body_temperature['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_TEMPERATURE_TYPE_ID)[0], 'avgValue', 'ºC')
        temperatureStatsData.body_temperature['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === BODY_TEMPERATURE_TYPE_ID), 'value', 'ºC')

        this.temperatureStatsData = {...temperatureStatsData}
      }
      this.loading.temperature = false
    },
    parseBodyCompositionData: function (data) {
      const BODY_WEIGHT_TYPE_ID = 1
      const BODY_MASS_INDEX_TYPE_ID = 8
      const BODY_WATER_PERCENTAGE = 7
      const BODY_FAT_PERCENTAGE = 6
      const BODY_MUSCLE_MASS = 53

      this.bodyCompositionData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }
      data.forEach(elem => {
        this.bodyCompositionData.push({
          id: elem.idMeasurement,
          measurementTypeId: BODY_COMPOSITION_MEASUREMENT_TYPE_ID,
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          notes: null,
          body_weight: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === BODY_WEIGHT_TYPE_ID)[0], 'Kg'),
          body_mass_index: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === BODY_MASS_INDEX_TYPE_ID)[0]),
          body_water_percentage: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === BODY_WATER_PERCENTAGE)[0], '%'),
          body_fat_percentage: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === BODY_FAT_PERCENTAGE)[0], '%'),
          body_muscle_mass: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === BODY_MUSCLE_MASS)[0], '%'),
          last_chart_result: lastChartResultStr,
          automatic: elem.automatic,
        })
      })
      if (data.length > 0) {
        let bodyCompositionStatsData = {
          'body_weight': {},
          'body_mass_index': {},
          'body_water_percentage': {},
          'body_fat_percentage': {},
          'body_muscle_mass': {}
        }
        bodyCompositionStatsData.body_weight['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_WEIGHT_TYPE_ID)[0], 'maxValue', 'Kg')
        bodyCompositionStatsData.body_weight['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_WEIGHT_TYPE_ID)[0], 'minValue', 'Kg')
        bodyCompositionStatsData.body_weight['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_WEIGHT_TYPE_ID)[0], 'avgValue', 'Kg')

        bodyCompositionStatsData.body_mass_index['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MASS_INDEX_TYPE_ID)[0], 'maxValue', '')
        bodyCompositionStatsData.body_mass_index['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MASS_INDEX_TYPE_ID)[0], 'minValue', '')
        bodyCompositionStatsData.body_mass_index['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MASS_INDEX_TYPE_ID)[0], 'avgValue', '')

        bodyCompositionStatsData.body_water_percentage['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_WATER_PERCENTAGE)[0], 'maxValue', '%')
        bodyCompositionStatsData.body_water_percentage['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_WATER_PERCENTAGE)[0], 'minValue', '%')
        bodyCompositionStatsData.body_water_percentage['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_WATER_PERCENTAGE)[0], 'avgValue', '%')

        bodyCompositionStatsData.body_fat_percentage['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_FAT_PERCENTAGE)[0], 'maxValue', '%')
        bodyCompositionStatsData.body_fat_percentage['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_FAT_PERCENTAGE)[0], 'minValue', '%')
        bodyCompositionStatsData.body_fat_percentage['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_FAT_PERCENTAGE)[0], 'avgValue', '%')

        bodyCompositionStatsData.body_muscle_mass['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MUSCLE_MASS)[0], 'maxValue', '%')
        bodyCompositionStatsData.body_muscle_mass['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MUSCLE_MASS)[0], 'minValue', '%')
        bodyCompositionStatsData.body_muscle_mass['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MUSCLE_MASS)[0], 'avgValue', '%')

        bodyCompositionStatsData.body_weight['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === BODY_WEIGHT_TYPE_ID), 'value', 'Kg')
        bodyCompositionStatsData.body_mass_index['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === BODY_MASS_INDEX_TYPE_ID), 'value', '')
        bodyCompositionStatsData.body_water_percentage['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === BODY_WATER_PERCENTAGE), 'value', '%')
        bodyCompositionStatsData.body_fat_percentage['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === BODY_FAT_PERCENTAGE), 'value', 'ºC')
        bodyCompositionStatsData.body_muscle_mass['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === BODY_MUSCLE_MASS), 'value', 'ºC')
        this.bodyCompositionStatsData = {...bodyCompositionStatsData}
      }
      this.loading.bodyComposition = false
    },
    parseSarcopeniaData: function (data) {
      const SARC_F_TYPE_ID = 6050
      const HAND_GRIP_TYPE_ID = 6004
      const BODY_MUSCLE_MASS_TYPE_ID = 6052
      const GAIT_SPEED_TYPE_ID = 6003
      const SARC_RESULT_TYPE_ID = 6055

      this.sarcopeniaData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }
      data.forEach(elem => {
        if (elem.measurements !== null) {
          this.sarcopeniaData.push({
            id: elem.idMeasurement,
            measurementSeries: elem.measurementSeries,
            measurementTypeId: SARCOPENIA_MEASUREMENT_TYPE_ID,
            created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
            updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
            notes: null,
            sarc_f: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === SARC_F_TYPE_ID)[0], ''),
            hand_grip: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === HAND_GRIP_TYPE_ID)[0], 'kgf'),
            body_muscle_mass: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === BODY_MUSCLE_MASS_TYPE_ID)[0], 'Kg'),
            gait_speed: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === GAIT_SPEED_TYPE_ID)[0], 's'),
            sarc_result: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === SARC_RESULT_TYPE_ID)[0], ''),
            last_chart_result: lastChartResultStr,
            automatic: elem.automatic,
          })
        }
      })

      if (data.length > 0) {
        let sarcopeniaStatsData = {
          'sarc_f': {},
          'hand_grip': {},
          'body_muscle_mass': {},
          'gait_speed': {},
          'sarc_result': {}
        }
        sarcopeniaStatsData.sarc_f['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SARC_F_TYPE_ID)[0], 'maxValue', '')
        sarcopeniaStatsData.sarc_f['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SARC_F_TYPE_ID)[0], 'minValue', '')
        sarcopeniaStatsData.sarc_f['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SARC_F_TYPE_ID)[0], 'avgValue', '')

        sarcopeniaStatsData.hand_grip['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HAND_GRIP_TYPE_ID)[0], 'maxValue', 'kgf')
        sarcopeniaStatsData.hand_grip['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HAND_GRIP_TYPE_ID)[0], 'minValue', 'kgf')
        sarcopeniaStatsData.hand_grip['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HAND_GRIP_TYPE_ID)[0], 'avgValue', 'kgf')

        sarcopeniaStatsData.body_muscle_mass['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MUSCLE_MASS_TYPE_ID)[0], 'maxValue', 'Kg')
        sarcopeniaStatsData.body_muscle_mass['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MUSCLE_MASS_TYPE_ID)[0], 'minValue', 'Kg')
        sarcopeniaStatsData.body_muscle_mass['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BODY_MUSCLE_MASS_TYPE_ID)[0], 'avgValue', 'Kg')

        sarcopeniaStatsData.gait_speed['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === GAIT_SPEED_TYPE_ID)[0], 'maxValue', 's')
        sarcopeniaStatsData.gait_speed['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === GAIT_SPEED_TYPE_ID)[0], 'minValue', 's')
        sarcopeniaStatsData.gait_speed['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === GAIT_SPEED_TYPE_ID)[0], 'avgValue', 's')

        sarcopeniaStatsData.sarc_result['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SARC_RESULT_TYPE_ID)[0], 'maxValue', '')
        sarcopeniaStatsData.sarc_result['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SARC_RESULT_TYPE_ID)[0], 'minValue', '')
        sarcopeniaStatsData.sarc_result['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === SARC_RESULT_TYPE_ID)[0], 'avgValue', '')

        sarcopeniaStatsData.sarc_f['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === SARC_F_TYPE_ID), 'value', '')
        sarcopeniaStatsData.hand_grip['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === HAND_GRIP_TYPE_ID), 'value', 'kgf')
        sarcopeniaStatsData.body_muscle_mass['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === BODY_MUSCLE_MASS_TYPE_ID), 'value', 'Kg')
        sarcopeniaStatsData.gait_speed['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === GAIT_SPEED_TYPE_ID), 'value', 's')
        sarcopeniaStatsData.sarc_result['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === SARC_RESULT_TYPE_ID), 'value', '')
        this.sarcopeniaStatsData = {...sarcopeniaStatsData}
      }
      this.loading.sarcopenia = false
    },
    parseFrailtyPhenotypeData: function (data) {
      const FRAILTY_ACCESSMENT_RESULT_TYPE_ID = 6000
      const FRAILTY_EXHAUSTION_TYPE_ID = 6001
      const FRAILTY_PHYSICAL_ACTIVITY_TYPE_ID = 6002
      const FRAILTY_WALK_TIME_TYPE_ID = 6003
      const FRAILTY_DOMINANT_STRENGTH_TYPE_ID = 6004
      const FRAILTY_WEIGHT_LOSS_TYPE_ID = 6005

      this.frailtyPhenotypeData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }
      data.forEach(elem => {
        this.frailtyPhenotypeData.push({
          id: elem.idMeasurement,
          measurementSeries: elem.measurementSeries,
          measurementTypeId: FRAILTY_PHENOTYPE_TYPE_ID,
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          notes: null,
          frailty_accessment_result: this.$t(`dashboard_new_evollu.donut_chart_labels.frailty.${this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === FRAILTY_ACCESSMENT_RESULT_TYPE_ID)[0], '').toLowerCase().replace('-', '_').trim()}`),
          frailty_exhaustion_result: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === FRAILTY_EXHAUSTION_TYPE_ID)[0], ''),
          frailty_physical_activity: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === FRAILTY_PHYSICAL_ACTIVITY_TYPE_ID)[0], 'kcal'),
          frailty_walk_time: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === FRAILTY_WALK_TIME_TYPE_ID)[0], 's', true),
          frailty_dominant_strength: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === FRAILTY_DOMINANT_STRENGTH_TYPE_ID)[0], 'kgf'),
          frailty_weight_loss: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === FRAILTY_WEIGHT_LOSS_TYPE_ID)[0], 'kg', true),
          last_chart_result: lastChartResultStr,
          automatic: elem.automatic,
        })
      })

      if (data.length > 0) {
        let frailtyPhenotypeStatsData = {
          'frailty_exhaustion_result': {},
          'frailty_physical_activity': {},
          'frailty_walk_time': {},
          'frailty_dominant_strength': {},
          'frailty_weight_loss': {}
        }
        frailtyPhenotypeStatsData.frailty_exhaustion_result['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_EXHAUSTION_TYPE_ID)[0], 'maxValue', '')
        frailtyPhenotypeStatsData.frailty_exhaustion_result['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_EXHAUSTION_TYPE_ID)[0], 'minValue', '')
        frailtyPhenotypeStatsData.frailty_exhaustion_result['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_EXHAUSTION_TYPE_ID)[0], 'avgValue', '')

        frailtyPhenotypeStatsData.frailty_physical_activity['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_PHYSICAL_ACTIVITY_TYPE_ID)[0], 'maxValue', 'kcal')
        frailtyPhenotypeStatsData.frailty_physical_activity['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_PHYSICAL_ACTIVITY_TYPE_ID)[0], 'minValue', 'kcal')
        frailtyPhenotypeStatsData.frailty_physical_activity['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_PHYSICAL_ACTIVITY_TYPE_ID)[0], 'avgValue', 'kcal')

        frailtyPhenotypeStatsData.frailty_walk_time['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_WALK_TIME_TYPE_ID)[0], 'maxValue', 's')
        frailtyPhenotypeStatsData.frailty_walk_time['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_WALK_TIME_TYPE_ID)[0], 'minValue', 's')
        frailtyPhenotypeStatsData.frailty_walk_time['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_WALK_TIME_TYPE_ID)[0], 'avgValue', 's')

        frailtyPhenotypeStatsData.frailty_dominant_strength['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_DOMINANT_STRENGTH_TYPE_ID)[0], 'maxValue', 'kgf')
        frailtyPhenotypeStatsData.frailty_dominant_strength['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_DOMINANT_STRENGTH_TYPE_ID)[0], 'minValue', 'kgf')
        frailtyPhenotypeStatsData.frailty_dominant_strength['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_DOMINANT_STRENGTH_TYPE_ID)[0], 'avgValue', 'kgf')

        frailtyPhenotypeStatsData.frailty_weight_loss['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_WEIGHT_LOSS_TYPE_ID)[0], 'maxValue', 'kg')
        frailtyPhenotypeStatsData.frailty_weight_loss['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_WEIGHT_LOSS_TYPE_ID)[0], 'minValue', 'kg')
        frailtyPhenotypeStatsData.frailty_weight_loss['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === FRAILTY_WEIGHT_LOSS_TYPE_ID)[0], 'avgValue', 'kg')


        frailtyPhenotypeStatsData.frailty_exhaustion_result['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FRAILTY_EXHAUSTION_TYPE_ID), 'value', '')
        frailtyPhenotypeStatsData.frailty_physical_activity['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FRAILTY_PHYSICAL_ACTIVITY_TYPE_ID), 'value', 'kcal')
        frailtyPhenotypeStatsData.frailty_walk_time['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FRAILTY_WALK_TIME_TYPE_ID), 'value', 's')
        frailtyPhenotypeStatsData.frailty_dominant_strength['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FRAILTY_DOMINANT_STRENGTH_TYPE_ID), 'value', 'kgf')
        frailtyPhenotypeStatsData.frailty_weight_loss['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === FRAILTY_WEIGHT_LOSS_TYPE_ID), 'value', 'kg')
        this.frailtyPhenotypeStatsData = {...frailtyPhenotypeStatsData}
      }
      this.loading.frailtyPhenotype = false
    },
    parseGlucoseData: function (data) {
      const BLOOD_GLUCOSE_TYPE_ID = 5
      const FASTING_TYPE_ID = 58
      this.glucoseData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }
      data.forEach(elem => {
        this.glucoseData.push({
          id: elem.idMeasurement,
          measurementTypeId: GLUCOSE_MEASUREMENT_TYPE_ID,
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          blood_glucose: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === BLOOD_GLUCOSE_TYPE_ID)[0], 'mg/dL'),
          fasting: this.parseBoolean(this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === FASTING_TYPE_ID)[0], '')),
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr,
          notes: null,
        })
      })

      if (data.length > 0) {
        let glucoseStatsData = {
          'blood_glucose': {},
          'fasting': {},
        }
        glucoseStatsData.blood_glucose['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BLOOD_GLUCOSE_TYPE_ID)[0], 'maxValue', 'mg/dL')
        glucoseStatsData.blood_glucose['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BLOOD_GLUCOSE_TYPE_ID)[0], 'minValue', 'mg/dL')
        glucoseStatsData.blood_glucose['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BLOOD_GLUCOSE_TYPE_ID)[0], 'avgValue', 'mg/dL')

        glucoseStatsData.fasting['max'] = this.parseBoolean(this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BLOOD_GLUCOSE_TYPE_ID)[0], 'maxValue', ''))
        glucoseStatsData.fasting['min'] = this.parseBoolean(this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BLOOD_GLUCOSE_TYPE_ID)[0], 'minValue', ''))
        glucoseStatsData.fasting['avg'] = this.parseBoolean(this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === BLOOD_GLUCOSE_TYPE_ID)[0], 'avgValue', ''))


        glucoseStatsData.blood_glucose['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === BLOOD_GLUCOSE_TYPE_ID), 'value', 'mg/dL')
        glucoseStatsData.fasting['last'] = this.parseBoolean(this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.id === BLOOD_GLUCOSE_TYPE_ID), 'value', ''))

        this.glucoseStatsData = {...glucoseStatsData}
      }
      this.loading.glucose = false
    },
    parseCholesterolData: function (data) {
      const CHOLESTEROL_TOTAL_TYPE_ID = 10001
      const HDL_CHOLESTEROL_TYPE_ID = 10002
      const LDL_CHOLESTEROL_TYPE_ID = 10003
      const TRIGLYCERIDES_TYPE_ID = 10004
      this.cholesterolData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }
      data.forEach(elem => {
        this.cholesterolData.push({
          id: elem.idMeasurement,
          measurementTypeId: CHOLESTEROL_MEASUREMENT_TYPE_ID,
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          cholesterol_total: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === CHOLESTEROL_TOTAL_TYPE_ID)[0], 'mg/dL'),
          hdl_cholesterol: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === HDL_CHOLESTEROL_TYPE_ID)[0], 'mg/dL'),
          ldl_cholesterol: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === LDL_CHOLESTEROL_TYPE_ID)[0], 'mg/dL'),
          triglycerides: this.checkStringValueNotUndefinedAndReturn(elem.measurements.filter(elem => elem.typeId === TRIGLYCERIDES_TYPE_ID)[0], 'mg/dL'),
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr,
          notes: null
        })
      })
      if (data.length > 0) {
        let cholesterolStatsData = {
          'cholesterol_total': {},
          'hdl_cholesterol': {},
          'ldl_cholesterol': {},
          'triglycerides': {}
        }
        cholesterolStatsData.cholesterol_total['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === CHOLESTEROL_TOTAL_TYPE_ID)[0], 'maxValue', 'mg/dL')
        cholesterolStatsData.cholesterol_total['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === CHOLESTEROL_TOTAL_TYPE_ID)[0], 'minValue', 'mg/dL')
        cholesterolStatsData.cholesterol_total['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === CHOLESTEROL_TOTAL_TYPE_ID)[0], 'avgValue', 'mg/dL')

        cholesterolStatsData.hdl_cholesterol['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HDL_CHOLESTEROL_TYPE_ID)[0], 'maxValue', 'mg/dL')
        cholesterolStatsData.hdl_cholesterol['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HDL_CHOLESTEROL_TYPE_ID)[0], 'minValue', 'mg/dL')
        cholesterolStatsData.hdl_cholesterol['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === HDL_CHOLESTEROL_TYPE_ID)[0], 'avgValue', 'mg/dL')

        cholesterolStatsData.ldl_cholesterol['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === LDL_CHOLESTEROL_TYPE_ID)[0], 'maxValue', 'mg/dL')
        cholesterolStatsData.ldl_cholesterol['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === LDL_CHOLESTEROL_TYPE_ID)[0], 'minValue', 'mg/dL')
        cholesterolStatsData.ldl_cholesterol['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === LDL_CHOLESTEROL_TYPE_ID)[0], 'avgValue', 'mg/dL')

        cholesterolStatsData.triglycerides['max'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === TRIGLYCERIDES_TYPE_ID)[0], 'maxValue', 'mg/dL')
        cholesterolStatsData.triglycerides['min'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === TRIGLYCERIDES_TYPE_ID)[0], 'minValue', 'mg/dL')
        cholesterolStatsData.triglycerides['avg'] = this.checkValueNotUndefinedAndReturn(data[0].measurementGroup.measurementTypes.filter(elem => elem.id === TRIGLYCERIDES_TYPE_ID)[0], 'avgValue', 'mg/dL')

        cholesterolStatsData.cholesterol_total['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === CHOLESTEROL_TOTAL_TYPE_ID), 'value', 'mg/dL')
        cholesterolStatsData.hdl_cholesterol['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === HDL_CHOLESTEROL_TYPE_ID), 'value', 'mg/dL')
        cholesterolStatsData.ldl_cholesterol['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === LDL_CHOLESTEROL_TYPE_ID), 'value', 'mg/dL')
        cholesterolStatsData.triglycerides['last'] = this.checkValueNotUndefinedAndReturn(data[0].measurements.find(elem => elem.typeId === TRIGLYCERIDES_TYPE_ID), 'value', 'mg/dL')
        this.cholesterolStatsData = {...cholesterolStatsData}
      }
      this.loading.cholesterol = false
    },
    parseMapa24Data: function (data) {
      const MAPA_24H_SYSTOLE_TYPE_ID = 62
      const MAPA_24H_DIASTOLE_TYPE_ID = 63
      const MAPA_24H_PULSE_TYPE_ID = 64
      const MAPA_24H_TYPE_TYPE_ID = 65
      const MAPA_24H_MAP_TYPE_ID = 66
      const MAPA_24H_EVENT_TYPE_ID = 67
      const MAPA_24H_ERROR_TYPE_ID = 68

      this.mapa24Data = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }
      data.forEach(elem => {

        this.mapa24Data.push({
          id: elem.idMeasurement,
          measurementSeries: elem.measurementSeries,
          measurementTypeId: MAPA_24_MEASUREMENT_TYPE_ID,
          measurementType: 'mapa24',
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr,
          notes: null
        })
      })

      this.loading.mapa24 = false
    },
    parseOptometryData: function (data) {
      //Type ids questionnaire
      const SURVEY_Q1_TYPE_ID = 9061;
      const SURVEY_Q2_TYPE_ID = 9062;
      const SURVEY_Q3_TYPE_ID = 9063;
      const SURVEY_Q4_TYPE_ID = 9064;
      const SURVEY_Q5_TYPE_ID = 9065;
      const SURVEY_Q6_TYPE_ID = 9066;
      const SURVEY_Q7_TYPE_ID = 9067;
      const SURVEY_Q8_TYPE_ID = 9068;
      const OPTOMETRY_RESULT_TYPE_ID = 9069;

      //Type ids test
      const ASTIGMATISM_LEFT_TYPE_ID = 9050;
      const ASTIGMATISM_RIGHT_TYPE_ID = 9051;
      const DUOCHROME_TYPE_ID = 9052;
      const CHROMATIC_WRONG_TYPE_ID = 9053;
      const CHROMATIC_CORRECT_TYPE_ID = 9054;
      const CONTRAST_LEFT_TYPE_ID = 9055;
      const CONTRAST_RIGHT_TYPE_ID = 9056;
      const MACULAR_LEFT_TYPE_ID = 9057;
      const MACULAR_RIGHT_TYPE_ID = 9058;
      const ACUITY_LEFT_TYPE_ID = 9059;
      const ACUITY_RIGHT_TYPE_ID = 9060;

      this.optometryData = []
      const lastChartResults = data[0].chartResults
      let lastChartResultStr = "N/A"
      if(lastChartResults.length > 0) {
        lastChartResultStr= this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`)
      }
      data.forEach(elem => {

        this.optometryData.push({
          id: elem.idMeasurement,
          measurementSeries: elem.measurementSeries,
          measurementTypeId: OPTOMETRY_MEASUREMENT_TYPE_ID,
          measurementType: 'optometry',
          created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
          automatic: elem.automatic,
          last_chart_result: lastChartResultStr,
          notes: null
        })
      })
      this.loading.optometry = false
    },
    updateMeasurementTypeFromId: function (measurementTypeId) {
      this.getMeasurementDataFromTypeId(measurementTypeId)
    },
    getMeasurementDataFromTypeId: async function (measurementTypeId) {
      const data = await OneCareService.getPatientMeasurements(this.$route.params.id, measurementTypeId, measurementTypeId !== FRAILTY_PHENOTYPE_TYPE_ID)
      if (data.objects.length > 0) {
        switch (data.objects[0].measurementGroup.idMeasurementGroup) {
          case BLOOD_PRESSURE_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: BLOOD_PRESSURE_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'bloodPressure',
              measurementTypes: [
                { typeId: 2, label: 'systolic', unit: 'mmHg', ignoreForStats: false },
                { typeId: 3, label: 'diastolic', unit: 'mmHg', ignoreForStats: false },
                { typeId: 4, label: 'heart_rate', unit: 'bpm', ignoreForStats: false },
              ]
            });
            //this.parseBloodPressureData(data.objects)
            break;
          case OXIMETRY_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: OXIMETRY_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'oximetry',
              measurementTypes: [
                { typeId: 46, label: 'duration_session', unit: 'min', ignoreForStats: false },
                { typeId: 47, label: 'oximetry_max', unit: '%', ignoreForStats: true },
                { typeId: 48, label: 'oximetry_min', unit: '%', ignoreForStats: true },
                { typeId: 49, label: 'oximetry_avg', unit: '%', ignoreForStats: false },
                { typeId: 50, label: 'pulse_rate_max', unit: 'bpm', ignoreForStats: true },
                { typeId: 51, label: 'pulse_rate_min', unit: 'bpm', ignoreForStats: true },
                { typeId: 52, label: 'pulse_rate_avg', unit: 'bpm', ignoreForStats: false },
              ],
            });
            break;
          case SPIROMETRY_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: SPIROMETRY_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'spirometry',
              measurementTypes: [
                { typeId: 21, label: 'forced_expiratory_volume_6_sec', unit: 'L', ignoreForStats: false },
                { typeId: 22, label: 'forced_expiratory_volume_1_sec', unit: 'L', ignoreForStats: false },
                { typeId: 23, label: 'fev1_fev6', unit: '', ignoreForStats: false },
                { typeId: 57, label: 'forced_mid_expiratory_flow_25_75', unit: 'L/s', ignoreForStats: false },
              ],
            });
            break;
          case TEMPERATURE_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: TEMPERATURE_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'temperature',
              measurementTypes: [
                { typeId: 11, label: 'body_temperature', unit: 'ºC', ignoreForStats: false },
              ],
            });
            break;
          case BODY_COMPOSITION_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: BODY_COMPOSITION_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'bodyComposition',
              measurementTypes: [
                { typeId: 1, label: 'body_weight', unit: 'Kg', ignoreForStats: false },
                { typeId: 8, label: 'body_mass_index', ignoreForStats: false },
                { typeId: 7, label: 'body_water_percentage', unit: '%', ignoreForStats: false },
                { typeId: 6, label: 'body_fat_percentage', unit: '%', ignoreForStats: false },
                { typeId: 53, label: 'body_muscle_mass', unit: '%', ignoreForStats: false },
              ],
            });
            break;
          case SARCOPENIA_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: SARCOPENIA_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'sarcopenia',
              measurementTypes: [
                { typeId: 6050, label: 'sarc_f', ignoreForStats: false },
                { typeId: 6004, label: 'hand_grip', unit: 'kgf', ignoreForStats: false },
                { typeId: 6052, label: 'body_muscle_mass', unit: 'Kg', ignoreForStats: false },
                { typeId: 6003, label: 'gait_speed', unit: 's', ignoreForStats: false },
                { typeId: 6055, label: 'sarc_result', ignoreForStats: false },
              ],
            });
            break;
          case FRAILTY_PHENOTYPE_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: FRAILTY_PHENOTYPE_TYPE_ID,
              measurementTypeLabel: 'frailtyPhenotype',
              measurementTypes: [
                { typeId: 6000, label: 'frailty_accessment_result', unit: '', ignoreForStats: true,  needsTranslation: true },
                { typeId: 6001, label: 'frailty_exhaustion_result', unit: '', ignoreForStats: false },
                { typeId: 6002, label: 'frailty_physical_activity', unit: 'kcal', ignoreForStats: false },
                { typeId: 6003, label: 'frailty_walk_time', unit: 's', round: true, ignoreForStats: false },
                { typeId: 6004, label: 'frailty_dominant_strength', unit: 'kgf', ignoreForStats: false },
                { typeId: 6005, label: 'frailty_weight_loss', unit: 'kg', round: true, ignoreForStats: false },
              ],
            });
            break;
          case GLUCOSE_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: GLUCOSE_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'glucose',
              measurementTypes: [
                { typeId: 5, label: 'blood_glucose', unit: 'mg/dL', ignoreForStats: false },
                { typeId: 58, label: 'fasting', isBoolean: true, ignoreForStats: false },
              ],
            })
            break;
          case CHOLESTEROL_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: CHOLESTEROL_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'cholesterol',
              measurementTypes: [
                { typeId: 10001, label: 'cholesterol_total', unit: 'mg/dL', ignoreForStats: false },
                { typeId: 10002, label: 'hdl_cholesterol', unit: 'mg/dL', ignoreForStats: false },
                { typeId: 10003, label: 'ldl_cholesterol', unit: 'mg/dL', ignoreForStats: false },
                { typeId: 10004, label: 'triglycerides', unit: 'mg/dL', ignoreForStats: false },
              ],
              measurementStatsData: this.glucoseStatsData,
            })
            break;
          case MAPA_24_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: MAPA_24_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'mapa24',
              measurementTypes: [],
              parseStatsData: false
            })
            break;
          case OPTOMETRY_MEASUREMENT_TYPE_ID:
            //this.parseOptometryData(data.objects)
            this.parseMeasurementData(data.objects, {
              measurementTypeId: OPTOMETRY_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'optometry',
              measurementTypes: [
                {typeId: 9050, label: 'astigmatism_left', unit: '', isBoolean: true, ignoreForStats: true},
                {typeId: 9051, label: 'astigmatism_right', unit: '', isBoolean: true, ignoreForStats: true},
                {typeId: 9052, label: 'duochrome', unit: '', isBoolean: true, ignoreForStats: true},
                {typeId: 9053, label: 'chromatic_wrong', unit: '', isBoolean: false, ignoreForStats: true},
                {typeId: 9054, label: 'chromatic_correct', unit: '', isBoolean: false, ignoreForStats: true},
                {typeId: 9055, label: 'contrast_left', unit: '%', isBoolean: false, ignoreForStats: true},
                {typeId: 9056, label: 'contrast_right', unit: '%', isBoolean: false, ignoreForStats: true},
                {typeId: 9057, label: 'macular_left', unit: '', isBoolean: true, ignoreForStats: true},
                {typeId: 9058, label: 'macular_right', unit: '', isBoolean: true, ignoreForStats: true},
                {typeId: 9059, label: 'acuity_left', unit: '%', isBoolean: false, ignoreForStats: true},
                {typeId: 9060, label: 'acuity_right', unit: '%', isBoolean: false, ignoreForStats: true},
                {typeId: 9069, label: 'optometry_result', unit: '', isBoolean: true, ignoreForStats: false}
              ],
              parseStatsData: true
            })
            break;
          case BODY_CIRCUMFERENCE_MEASUREMENT_TYPE_ID:
            this.parseMeasurementData(data.objects, {
              measurementTypeId: BODY_CIRCUMFERENCE_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'bodyCircumference',
              measurementTypes: [
                {typeId: 69, label: 'waist_perimeter', unit: 'cm', isBoolean: false, ignoreForStats: false},
                {typeId: 70, label: 'abdomen_perimeter', unit: 'cm', isBoolean: false, ignoreForStats: false},
                {typeId: 71, label: 'chest_perimeter', unit: 'cm', isBoolean: false, ignoreForStats: false},
                {typeId: 72, label: 'relaxed_arm_perimeter', unit: 'cm', isBoolean: false, ignoreForStats: false},
                {typeId: 73, label: 'thigh_perimeter', unit: 'cm', isBoolean: false, ignoreForStats: false},
                {typeId: 74, label: 'calf_perimeter', unit: 'cm', isBoolean: false, ignoreForStats: false},
              ],
              parseStatsData: true
            })
            break;
          case CHECKUP_ANAMNESE_MEASUREMENT_TYPE_ID:
            //dashboard_new_evollu.measurement_types.questions.1190
            this.parseMeasurementData(data.objects, {
              measurementTypeId: CHECKUP_ANAMNESE_MEASUREMENT_TYPE_ID,
              measurementTypeLabel: 'checkupAnamnese',
              measurementTypes: [
                {typeId: 1186, label: 'question1186', unit: '', isBoolean: false, ignoreForStats: true},
                {typeId: 1187, label: 'question1187', unit: '', isBoolean: false, ignoreForStats: true},
                {typeId: 1188, label: 'question1188', unit: '', isBoolean: false, ignoreForStats: true},
                {typeId: 1189, label: 'question1189', unit: '', isBoolean: false, ignoreForStats: true},
                {typeId: 1190, label: 'question1190', unit: '', isBoolean: false, ignoreForStats: true},
              ],
              parseStatsData: false
            })

            break;
          default:
            console.error("Measure not handled")
        }
      } else {
        switch (measurementTypeId) {
          case BLOOD_PRESSURE_MEASUREMENT_TYPE_ID:
            this.loading.bloodPressure = false
            break;
          case OXIMETRY_MEASUREMENT_TYPE_ID:
            this.loading.oximetry = false
            break;
          case SPIROMETRY_MEASUREMENT_TYPE_ID:
            this.loading.spirometry = false
            break;
          case TEMPERATURE_MEASUREMENT_TYPE_ID:
            this.loading.temperature = false
            break;
          case BODY_COMPOSITION_MEASUREMENT_TYPE_ID:
            this.loading.bodyComposition = false
            break;
          case SARCOPENIA_MEASUREMENT_TYPE_ID:
            this.loading.sarcopenia = false
            break;
          case FRAILTY_PHENOTYPE_TYPE_ID:
            this.loading.frailtyPhenotype = false
            break;
          case GLUCOSE_MEASUREMENT_TYPE_ID:
            this.loading.glucose = false
            break;
          case CHOLESTEROL_MEASUREMENT_TYPE_ID:
            this.loading.cholesterol = false
            break;
          case MAPA_24_MEASUREMENT_TYPE_ID:
            this.loading.mapa24 = false
            break;
          case OPTOMETRY_MEASUREMENT_TYPE_ID:
            this.loading.optometry = false
            break;
          case BODY_CIRCUMFERENCE_MEASUREMENT_TYPE_ID:
            this.loading.bodyCircumference = false
            break;
          case CHECKUP_ANAMNESE_MEASUREMENT_TYPE_ID:
            this.loading.checkupAnamnese = false
            break;
          default:
            console.error("Measure not handled")
        }
      }
    },
    parseMeasurementData: function (data, measurementConfig) {
      const {
        measurementTypeId,
        measurementTypeLabel,
        measurementTypes,
        parseStatsData = true,
      } = measurementConfig;


      this[measurementTypeLabel + 'Data'] = [];
      const lastChartResults = data[0].chartResults;
      let lastChartResultStr = 'N/A';
      if (lastChartResults.length > 0) {
        lastChartResultStr = this.$t(`dashboard_new_evollu.parameters.graphs.legends.${lastChartResults[0].code.toLowerCase()}`);
      }
      let statsData = {};
      if(data.length > 0) {
        data.forEach(elem => {
          const measurementData = {
            id: elem.idMeasurement,
            measurementSeries: elem.measurementSeries,
            measurementTypeId,
            measurementType: measurementTypeLabel,
            created_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
            updated_at: moment(elem.date).format('YYYY-MM-DD HH:mm'),
            automatic: elem.automatic,
            last_chart_result: lastChartResultStr,
            notes: null,
          };

          measurementTypes.forEach(type => {
            let value = this.checkStringValueNotUndefinedAndReturn(
              elem.measurements ? elem.measurements.find(measurement => measurement.typeId === type.typeId) : 'N/A',
              type.unit,
              type.round,
              type.isBoolean
            );

            if(type.needsTranslation) {
              measurementData[type.label] = this.$t(`dashboard_new_evollu.donut_chart_labels.frailty.${value.toLowerCase().replace('-', '_').trim()}`)
            } else {
              measurementData[type.label] = value
            }

            if(parseStatsData && !type.ignoreForStats) {
              statsData[type.label] = {}
              statsData[type.label]['last'] = this.checkStringValueNotUndefinedAndReturn(
                data[0].measurements.find(measurement => measurement.typeId === type.typeId),
                type.unit,
                type.round,
                type.isBoolean
              );
            }
          });

          this[measurementTypeLabel + 'Data'].push(measurementData);
          this[measurementTypeLabel + 'StatsData'] = { ...statsData };

        });
      }

      this.loading[measurementTypeLabel] = false;
    },
    onConfirmDeletePatient: function () {
      OneCareService.deletePatient(this.patientDetails.idPerson)
        .then(() => {
          this.showToast(this.$t('dashboard_new_evollu.messages.successful_delete'), this.$store.state.successToastOptions)
          this.$router.push({path: '/check-up_plus/checkup_plus/patients'})
        })
        .catch(() => {
          this.showToast(this.$t('dashboard_new_evollu.messages.something_went_wrong'), this.$store.state.errorToastOptions)
        })
      this.showDeletePatient = false
    },
    onCancelDeletePatient: function () {
      this.showDeletePatient = false
    },
    onConfirmChangeStatus: function () {
      OneCareService.changePatientStatus(this.patientDetails.idPerson)
        .then(() => {
          this.showToast(this.$t('dashboard_new_evollu.messages.successful_status_change'), this.$store.state.successToastOptions)
          this.$router.push({path: '/check-up_plus/checkup_plus/patients'})
        })
        .catch(() => {
          this.showToast(this.$t('dashboard_new_evollu.messages.something_went_wrong_status_change'), this.$store.state.errorToastOptions)
        })
      this.showStatusChangePatientModal = false
    },
    onCancelChangeStatus: function () {
      this.showStatusChangePatientModal = false
    },
    checkValueNotUndefinedAndReturn: function (val, type, unit = '', isBoolean = false) {
      if (val && val[type]) {
        return isBoolean ? this.parseBoolean(`${val[type]} ${unit.toString()}`) : `${val[type]} ${unit.toString()}`
      } else return "--"
    },
    onCancelEdit: function () {
      this.showUpdatePatient = false
      this.loadData(false)
    },
    checkStringValueNotUndefinedAndReturn: function (val, unit = '', round = false, isBoolean = false) {
      if (val && val.stringValue) {
        if (round) {
          let roundedVal = this.roundStringValueToNearestTenth(val.stringValue)
          return isBoolean ? this.parseBoolean(`${roundedVal} ${unit}`) : `${roundedVal} ${unit}`
        } else {
          return isBoolean ? this.parseBoolean(`${val.stringValue} ${unit}`) : `${val.stringValue} ${unit}`
        }
      } else return "--"
    },
    roundStringValueToNearestTenth: function (val) {
      if (val === null || val === undefined || val === '') {
        return "--"
      } else if (!isNaN(val) && val.indexOf('.') != -1) {
        if (val.split(".")[1].length > 1) {
          let roundedFloat = Math.round(10 * parseFloat(val)) / 10
          return roundedFloat.toString()
        }
      }
      return val
    },
    parseBoolean: function (val) {
      const value = typeof val === "string" ? parseInt(val) : val
      return value === 1 ? this.$t('clinical.labels.yes') : this.$t('clinical.labels.no')
    },
    addUnderscoreToUppercase: function (inputString) {
      var modifiedString = "";

      for (var i = 0; i < inputString.length; i++) {
        var currentChar = inputString.charAt(i);

        if (currentChar === currentChar.toUpperCase() && /[A-Z]/.test(currentChar)) {
          // If the character is uppercase, add an underscore and the lowercase version
          modifiedString += "_" + currentChar.toLowerCase();
        } else {
          // If the character is not uppercase, keep it as is
          modifiedString += currentChar;
        }
      }

      return modifiedString;
    }
  },
  computed: {
    countries: function () {
      for (let code of this.$store.state.countries.allowed) {
        if (code.toLowerCase() === "gb") {
          code = "en"
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${code.toLowerCase()}.json`))
      }
      let tempCountries = countries.getNames(Vue.i18n.locale().toLowerCase() === 'gb' ? 'en' : Vue.i18n.locale().toLowerCase())
      let countriesList = []
      for(const key in tempCountries) {
        countriesList.push({
          name: tempCountries[key],
          code: key
        })
      }
      return countriesList
      //return CountryList.filter(country => this.$store.state.countries.allowed.includes(country.code))
      //return CountryList
    },
    selectedCardData: function () {
      if (this.selectedCardIdentifier) {
        return {
          identifier: this.addUnderscoreToUppercase(this.selectedCardIdentifier),
          originalIdentifier: this.selectedCardIdentifier,
          data: this[`${this.selectedCardIdentifier}Data`],
          statsData: this[`${this.selectedCardIdentifier}StatsData`],
          loading: this.loading[`${this.selectedCardIdentifier}`],
          chartData: this[`${this.selectedCardIdentifier}ChartData`] || {},
        }
      } else {
        return null
      }
    },
    measurementCardsIdentifiers: function () {
      const measurementsTypesIds = {
        audiology: TONAL_TEST_MEASUREMENT_TYPE_ID,
        bloodPressure: BLOOD_PRESSURE_MEASUREMENT_TYPE_ID,
        oximetry: OXIMETRY_MEASUREMENT_TYPE_ID,
        glucose: GLUCOSE_MEASUREMENT_TYPE_ID,
        spirometry: SPIROMETRY_MEASUREMENT_TYPE_ID,
        temperature: TEMPERATURE_MEASUREMENT_TYPE_ID,
        bodyComposition: BODY_COMPOSITION_MEASUREMENT_TYPE_ID,
        sarcopenia: SARCOPENIA_MEASUREMENT_TYPE_ID,
        frailtyPhenotype: FRAILTY_PHENOTYPE_TYPE_ID,
        cholesterol: CHOLESTEROL_MEASUREMENT_TYPE_ID,
        mapa24: MAPA_24_MEASUREMENT_TYPE_ID,
        optometry: OPTOMETRY_MEASUREMENT_TYPE_ID,
        bodyCircumference: BODY_CIRCUMFERENCE_MEASUREMENT_TYPE_ID,
        checkupAnamnese: CHECKUP_ANAMNESE_MEASUREMENT_TYPE_ID
      }
      const measurements = ['audiology', 'bloodPressure', 'oximetry', 'glucose', 'spirometry', 'temperature', 'bodyComposition', 'sarcopenia', 'frailtyPhenotype', 'cholesterol', 'mapa24', 'optometry', 'bodyCircumference', 'checkupAnamnese']
      return measurements.map(elem => {
        return {
          identifier: this.addUnderscoreToUppercase(elem),
          originalIdentifier: elem,
          data: this[`${elem}Data`],
          statsData: this[`${elem}StatsData`],
          loading: this.loading[`${elem}`],
          measurementTypeId: measurementsTypesIds[elem]
        }
      })
    },
    handlePatientName: function () {
      //patientDetails ? `${patientDetails.firstName} ${patientDetails.surname}` : ''
      if (this.patientDetails === null) return ''
      let name = ''
      this.patientDetails.firstName ? name = name.concat(this.patientDetails.firstName) : name = name.concat(this.patientDetails.name)
      if (this.patientDetails.surname) {
        name = name.concat(` ${this.patientDetails.surname}`)
      }
      return name
    },
    checkupPlusService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.onecare_server) || null
    },
    peopleManagerService: function() {
      return this.$store.state.services.find(service => service.url === this.$store.state.people_manager_server) || null
    },
    hasSomeActionsPermissions: function () {
      return this.canUpdateUser || this.canDeletePatient
    },
    canSeeMeasurements: function () {
      const patientEntityId = this.patientDetails.facilitiesEvollu[0].entity_id
      if (patientEntityId === null || patientEntityId === undefined) {
        return false
      }
      return this.checkupPlusService.user_permissions.some(p => p.entity_id === patientEntityId && p.access_type >= 2 && p.info_type === 120)
    },
    canUpdateUser: function () {
      //const patientEntities = this.patientDetails.facilitiesEvollu.map(elem => elem.entity_id)
      const patientEntityId = this.patientDetails.facilitiesEvollu[0].entity_id

      if (patientEntityId === null || patientEntityId === undefined || this.peopleManagerService === null || this.patientDetails.peopleManagerId === null) {
        return false
      }
      return this.peopleManagerService.user_permissions.some(p => p.access_type >= 2 && p.info_type === PEOPLE_MANAGER_LIST_INFO_TYPE && (p.entity_id === null || p.entity_id === parseInt(patientEntityId)))
    },
    canCreateMeasurement: function () {
      return this.canUpdateUser
    },
    canDeleteMeasurement: function () {
      return this.canUpdateUser
    },
    canDeletePatient: function () {
      const patientEntityId = this.patientDetails.facilitiesEvollu[0].entity_id
      if (patientEntityId === null || patientEntityId === undefined) {
        return false
      }
      return this.checkupPlusService.user_permissions.some(p => p.entity_id === patientEntityId && p.access_type >= 3 && p.info_type === 120)
    },
    hasPermissionToSendReportToPatient: function () {
      const patientEntityId = this.patientDetails.facilitiesEvollu[0].entity_id
      if (patientEntityId === null || patientEntityId === undefined) {
        return false
      }
      return this.checkupPlusService.user_permissions.some(p => p.entity_id === patientEntityId && p.access_type === 1 && p.info_type === 120)
    },
    hasPermissionToSendReportToPatientAndOthers: function () {
      const patientEntityId = this.patientDetails.facilitiesEvollu[0].entity_id
      if (patientEntityId === null || patientEntityId === undefined) {
        return false
      }
      return this.checkupPlusService.user_permissions.some(p => p.entity_id === patientEntityId && p.access_type >= 2 && p.info_type === 120)
    },
    bloodPressureChartData: function () {
      let reversedData = [...this.bloodPressureData]
      reversedData = reversedData.reverse()

      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.systolic'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.systolic)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.diastolic'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.diastolic)).reverse(),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.heart_rate'),
            color: '#74af8c',
            data: reversedData.map(elem => parseInt(elem.heart_rate)).reverse(),
          }
        ]
      }
    },
    oximetryChartData: function () {
      let reversedData = [...this.oximetryData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.duration_session_short'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.duration_session)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.oximetry_max_short'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.oximetry_max)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.oximetry_min_short'),
            color: '#74af8c',
            data: reversedData.map(elem => parseInt(elem.oximetry_min)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.oximetry_avg_short'),
            color: '#ffe6df',
            data: reversedData.map(elem => parseInt(elem.oximetry_avg)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.pulse_rate_max_short'),
            color: '#ad9983',
            data: reversedData.map(elem => parseInt(elem.pulse_rate_max)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.pulse_rate_min_short'),
            color: '#975ac5',
            data: reversedData.map(elem => parseInt(elem.pulse_rate_min)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.pulse_rate_avg_short'),
            color: '#de9347',
            data: reversedData.map(elem => parseInt(elem.pulse_rate_avg)),
          },
        ]
      }
    },
    spirometryChartData: function () {
      let reversedData = [...this.spirometryData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.forced_expiratory_volume_6_sec_short'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.forced_expiratory_volume_6_sec)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.forced_expiratory_volume_1_sec_short'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.forced_expiratory_volume_1_sec)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.fev1_fev6_short'),
            color: '#74af8c',
            data: reversedData.map(elem => parseInt(elem.fev1_fev6)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.forced_mid_expiratory_flow_25_75_short'),
            color: '#ffe6df',
            data: reversedData.map(elem => parseInt(elem.forced_mid_expiratory_flow_25_75)),
          },
        ]
      }
    },
    temperatureChartData: function () {
      let reversedData = [...this.temperatureData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.body_temperature_short'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.body_temperature)),
          }
        ]
      }
    },
    bodyCompositionChartData: function () {
      let reversedData = [...this.bodyCompositionData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.body_weight_short'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.body_weight)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.body_mass_index_short'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.body_mass_index)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.body_water_percentage_short'),
            color: '#74af8c',
            data: reversedData.map(elem => parseInt(elem.body_water_percentage)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.body_fat_percentage_short'),
            color: '#ffe6df',
            data: reversedData.map(elem => parseInt(elem.body_fat_percentage)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.body_muscle_mass_short'),
            color: '#ad9983',
            data: reversedData.map(elem => parseInt(elem.body_muscle_mass)),
          },
        ]
      }
    },
    glucoseChartData: function () {
      let reversedData = [...this.glucoseData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.blood_glucose_short'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.blood_glucose)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.fasting_short'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.fasting)),
          },
        ]
      }
    },
    sarcopeniaChartData: function () {
      let reversedData = [...this.sarcopeniaData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.sarc_f_short'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.sarc_f)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.hand_grip_short'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.hand_grip)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.body_muscle_mass_short'),
            color: '#74af8c',
            data: reversedData.map(elem => parseInt(elem.body_muscle_mass)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.gait_speed_short'),
            color: '#ffe6df',
            data: reversedData.map(elem => parseInt(elem.gait_speed)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.sarc_result_short'),
            color: '#ad9983',
            data: reversedData.map(elem => parseInt(elem.sarc_result)),
          },
        ]
      }
    },
    frailtyPhenotypeChartData: function () {
      let reversedData = [...this.frailtyPhenotypeData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.frailty_exhaustion_result_short'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.frailty_exhaustion_result)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.frailty_physical_activity_short'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.frailty_physical_activity)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.frailty_walk_time_short'),
            color: '#74af8c',
            data: reversedData.map(elem => parseInt(elem.frailty_walk_time)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.frailty_dominant_strength_short'),
            color: '#ffe6df',
            data: reversedData.map(elem => parseInt(elem.frailty_dominant_strength)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.frailty_weight_loss_short'),
            color: '#ad9983',
            data: reversedData.map(elem => parseInt(elem.frailty_weight_loss)),
          },
        ]
      }
    },
    cholesterolChartData: function () {
      let reversedData = [...this.cholesterolData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.cholesterol_total'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.cholesterol_total)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.hdl_cholesterol'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.hdl_cholesterol)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.ldl_cholesterol'),
            color: '#74af8c',
            data: reversedData.map(elem => parseInt(elem.ldl_cholesterol)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.triglycerides'),
            color: '#ffe6df',
            data: reversedData.map(elem => parseInt(elem.triglycerides)),
          },
        ]
      }
    },
    mapa24ChartData: function () {
      return []
    },
    bodyCircumferenceChartData: function () {
      let reversedData = [...this.bodyCircumferenceData]
      reversedData = reversedData.reverse()
      return {
        labels: reversedData.map(elem => elem.created_at),
        datasets: [
          {
            name: this.$t('dashboard_new_evollu.measurement_types.waist_perimeter_short'),
            color: '#9859a9',
            data: reversedData.map(elem => parseInt(elem.waist_perimeter)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.abdomen_perimeter_short'),
            color: '#66aca9',
            data: reversedData.map(elem => parseInt(elem.abdomen_perimeter)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.chest_perimeter_short'),
            color: '#74af8c',
            data: reversedData.map(elem => parseInt(elem.chest_perimeter)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.relaxed_arm_perimeter_short'),
            color: '#ffe6df',
            data: reversedData.map(elem => parseInt(elem.relaxed_arm_perimeter)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.thigh_perimeter_short'),
            color: '#ad9983',
            data: reversedData.map(elem => parseInt(elem.thigh_perimeter)),
          },
          {
            name: this.$t('dashboard_new_evollu.measurement_types.calf_perimeter_short'),
            color: '#de9347',
            data: reversedData.map(elem => parseInt(elem.calf_perimeter)),
          },
        ]
      }
    }

  },
}
</script>

<style lang="scss">
.selected-card-bg .row .va-card {
  background: #dee2e6;
}
</style>

<style scoped>
.em {
  font-style: italic;
}

.recent-update {
  color: #73d30a;
}


/*.overlay-loader {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  opacity: 0.8;
  filter: blur(5px);
  z-index: 2;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-message {
  position: relative;
  z-index: 3;
  font-size: 20px;
  color: black;
  text-align: center;
  filter: none;
}*/

.overlay-loader {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.blur-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  opacity: 0.8;
  filter: blur(5px);
  z-index: 1; /* Background layer */
}

.overlay-message {
  position: relative;
  z-index: 3; /* Ensure the message is above the blur */
  font-size: 20px;
  color: black;
  text-align: center;
}

</style>
